/*
 * actions.js
 *
 * Every onclick / onchange handler referenced from inside panel template
 * strings. Each one mutates `state` and calls `render()` to repaint.
 *
 * Because the panel templates use inline `onclick="foo(...)"`, the
 * handlers must be reachable as global identifiers at parse time.
 * `attachToWindow()` at the bottom of this file exposes them. When
 * we eventually refactor templates to addEventListener, this module
 * shrinks to a regular export list.
 *
 * Once Supabase is wired (migration plan step 7), each mutation here
 * also queues an upsert/insert/delete via lib/supabase.js. The UI
 * stays optimistic — mutate local state first, render, then sync.
 */

import { state } from './state.js';
import { render, switchTab, switchSubTab, renderModal } from './render.js';
import { hist, histFor, todayKey, dateToCycleKey, ankerDay,
         homeSegs, todayPrevCycleKey, spaarbedrag, freqM,
         sinkingFundFor, monthInputToCycleKey, applyTheme,
         curKey, isKijker, droomWeight, loonPendingCycleKey,
         abonnsForReflection,
         isAutoPrideActive, setPrideOptOutYear, clearPrideOptOut } from './helpers.js';
import {
  supabase, updateProfile, setupNewHousehold, updateHousehold,
  expenseStateToRow,
  incomeStateToRow,
  savingsGoalStateToRow,
  extraEntryStateToRow,
  patchProfile, profilePatchStateToDb,
  deleteAllPersonalData, deleteAllHouseholdData
} from './lib/supabase.js';
import { startOnboarding } from './lib/onboarding.js';
import { celebrate } from './lib/confetti.js';
import { downloadExport } from './lib/export.js';
import { parseImportFile, summarizeImport, applyImport } from './lib/import.js';
import { sendOrQueue } from './lib/sync-queue.js';
import { checkForUpdateNow } from './lib/pwa-update.js';

// ---------- Sync-helpers ----------
//
// Optimistische mutaties naar Supabase via lib/sync-queue.js. Lokale
// state.S muteert direct (snappy UI), de DB-write loopt via sendOrQueue:
// — online + OK    → write gaat door
// — netwerkfout    → queue, retry bij online/visibilitychange
// — offline        → meteen queue
// Bij geen sessie (state.userId == null, bv. seed-mode of pre-hydrate)
// short-circuiten de helpers zodat de UI offline blijft werken.

/** Bouw de write-context voor een vast/var item. Returns null als de
 *  combinatie (samen zonder huishouden, geen sessie, …) niet syncbaar is. */
function expenseWriteCtx(p, type, sortOrder) {
  if (!state.userId) return null;
  const isHousehold = p === 'samen';
  if (isHousehold && !state.householdId) return null;
  return {
    userId:      isHousehold ? null : state.userId,
    householdId: isHousehold ? state.householdId : null,
    scope:       type,
    sortOrder
  };
}

/** Geef een item dat nog geen __id heeft er één. Idempotent. */
function ensureId(obj) {
  if (!obj.__id && typeof crypto?.randomUUID === 'function') {
    obj.__id = crypto.randomUUID();
  }
  return obj.__id;
}

function syncExpenseUpsert(item, p, type, sortOrder) {
  const ctx = expenseWriteCtx(p, type, sortOrder);
  if (!ctx) return;
  const id = ensureId(item);
  if (!id) return;
  sendOrQueue('upsertExpense', expenseStateToRow(item, ctx));
}

function syncExpenseDelete(item) {
  if (!state.userId || !item?.__id) return;
  sendOrQueue('deleteExpense', item.__id);
}

function syncIncomeUpsert(item, sortOrder) {
  if (!state.userId) return;
  const id = ensureId(item);
  if (!id) return;
  sendOrQueue('upsertIncome', incomeStateToRow(item, { userId: state.userId, sortOrder }));
}

function syncIncomeDelete(item) {
  if (!state.userId || !item?.__id) return;
  sendOrQueue('deleteIncome', item.__id);
}

function syncWeeklyBudget(catId, amount) {
  if (!state.userId) return;
  sendOrQueue('upsertWeeklyBudget', state.userId, catId, amount);
}

function syncWeeklyBudgetDelete(catId) {
  if (!state.userId) return;
  sendOrQueue('deleteWeeklyBudget', state.userId, catId);
}

/** Persisteer een categorie-rij voor de ingelogde user. Categorieën zijn
 *  per-user opgeslagen (eigen kopie + slug als shared identifier), dus
 *  we updaten enkel onze eigen rij. */
function syncCategoryUpsert(cat) {
  if (!state.userId) return;
  sendOrQueue('upsertCategory', state.userId, cat);
}

function syncCategoryDelete(slug) {
  if (!state.userId) return;
  sendOrQueue('deleteCategory', state.userId, slug);
}

function syncSavingsGoalUpsert(item, sortOrder) {
  if (!state.userId) return;
  const id = ensureId(item);
  if (!id) return;
  sendOrQueue('upsertSavingsGoal', savingsGoalStateToRow(item, { userId: state.userId, sortOrder }));
}

function syncSavingsGoalDelete(item) {
  if (!state.userId || !item?.__id) return;
  sendOrQueue('deleteSavingsGoal', item.__id);
}

/** Sync alle dromen van de current user — gebruikt voor doSpaar/surplusKies
 *  die proportioneel over alle dromen verdelen (alle .gesp velden veranderen).
 *  Goedkoop want we hebben max ~5 dromen per persoon. */
function syncAllDromen() {
  if (!state.userId) return;
  const d = state.S[state.user];
  if (!d?.dromen) return;
  d.dromen.forEach((dr, i) => syncSavingsGoalUpsert(dr, i));
}

function syncExtraEntryPush(item, person, direction, cycleKey) {
  if (!state.userId) return;
  const isHousehold = person === 'samen';
  if (isHousehold && !state.householdId) return;
  const id = ensureId(item);
  if (!id) return;
  // vrKey ('var:3') → related_expense_id (UUID). Indexen kunnen schuiven
  // bij delete/reorder, maar de UUID is stabiel — daarom bewaren we 'm zo.
  let relatedExpenseId = null;
  if (item.vrKey) {
    const [vType, vIdxStr] = String(item.vrKey).split(':');
    const vIdx = parseInt(vIdxStr, 10);
    const arr = state.S[person]?.[vType];
    relatedExpenseId = arr?.[vIdx]?.__id || null;
  }
  sendOrQueue('upsertExtraEntry', extraEntryStateToRow(item, {
    userId:      isHousehold ? null : state.userId,
    householdId: isHousehold ? state.householdId : null,
    direction,
    cycleKey,
    relatedExpenseId
  }));
}

function syncExtraEntryDelete(item) {
  if (!state.userId || !item?.__id) return;
  sendOrQueue('deleteExtraEntry', item.__id);
}

/** Persisteer een gedeeltelijke profile-patch (camelCase JS keys → DB cols). */
function syncProfilePatch(patch) {
  if (!state.userId) return;
  const dbPatch = profilePatchStateToDb(patch);
  if (Object.keys(dbPatch).length === 0) return;
  sendOrQueue('patchProfile', state.userId, dbPatch);
}

/** Schulden / externe-schulden zijn JSONB-arrays op profile. Bij elke
 *  mutatie aan d.schulden / d.externeSchulden van de ingelogde user pushen
 *  we de volledige array opnieuw. Voor intra-household schulden (kant
 *  'naar-andere' / 'van-andere'): we syncen alleen ONZE kant — de partner
 *  picked het op bij z'n volgende hydrate. Bilateral live sync = stap 8. */
function syncSchulden() {
  if (state.person !== state.user) return;
  const d = state.S[state.user];
  if (!d) return;
  syncProfilePatch({ schulden: d.schulden || [] });
}

function syncExterneSchulden() {
  if (state.person !== state.user) return;
  const d = state.S[state.user];
  if (!d) return;
  syncProfilePatch({ externeSchulden: d.externeSchulden || [] });
}

// ---------- Kijker-modus guard ----------
// Schil rondom write-acties: in kijker-modus doen ze niets. Dit blokt
// per-ongeluk inline-onclick-calls die ondanks CSS-hiding zouden vuren.
function blockedByKijker() { return isKijker(); }

// ---------- Form toggles ----------

export function toggleForm(id) {
  if (blockedByKijker()) return;
  const el = document.getElementById(id);
  if (el) el.style.display = el.style.display === 'block' ? 'none' : 'block';
}

export function toggleWE(id) {
  const el = document.getElementById('we-' + id);
  if (el) el.style.display = el.style.display === 'block' ? 'none' : 'block';
}

// ---------- Weekly budgets (per categorie) ----------

export function saveWE(catId) {
  const d = state.S[state.person];
  if (!d.weekBudgets) d.weekBudgets = {};
  const v = parseFloat(document.getElementById('wb-' + catId).value);
  if (!isNaN(v) && v >= 0) {
    d.weekBudgets[catId] = v;
    // Sync — alleen voor jezelf, niet voor samen/mate (weekly_budgets is
    // strikt user-scoped in de DB).
    if (state.person === state.user) syncWeeklyBudget(catId, v);
  }
  render();
}

// ---------- Vast/var items ----------

export function toggleEdit(ek, type, p) {
  state.editing[ek] = !state.editing[ek];
  // Vast/var-items leven sinds 2026-06-20 als sub-tabs binnen Uitgaven.
  // Forceer dat we op de juiste sub-tab staan voor de edit-box zichtbaar is.
  const subId = type === 'vast'
    ? (p === 'samen' ? 'svast' : 'vast')
    : (p === 'samen' ? 'svar'  : 'variabel');
  switchSubTab(subId);
}

export function saveEdit(ek, type, p, i) {
  const arr = type === 'vast' ? state.S[p].vast : state.S[p].var;
  const v = parseFloat(document.getElementById('ei-' + ek).value) || 0;
  const c = document.getElementById('ec-' + ek)?.value;
  const dom = document.getElementById('ed-' + ek)?.checked;
  const fSel = document.getElementById('ef-' + ek)?.value;
  if (v > 0) arr[i].a = v;
  if (c)     arr[i].cat = c;
  if (dom !== undefined) arr[i].domicilie = dom;
  // Fase C: betaaldag (1-31) — voor vast én var-items met vaste datum
  // (bv. abonnement-domiciliëringen op de 15e). Leeg = fallback naar anker-dag.
  const bdRaw = document.getElementById('ebd-' + ek)?.value;
  const bd = parseInt(bdRaw);
  if (bd >= 1 && bd <= 31) arr[i].betaalDag = bd;
  else delete arr[i].betaalDag;
  if (fSel) {
    const newFreq = fSel === 'custom'
      ? Math.max(1, parseInt(document.getElementById('efcN-' + ek).value) || 1)
      : parseInt(fSel) || 1;
    arr[i].freqM = newFreq;
    delete arr[i].f;  // legacy veld opruimen wanneer we 'm toch al bewerken
  }
  // lastPaidCycle (alleen relevant voor niet-maandelijkse items)
  const lpRaw = document.getElementById('elp-' + ek)?.value;
  const effectiveFreq = arr[i].freqM ?? freqM(arr[i]);
  if (effectiveFreq > 1) {
    arr[i].lastPaidCycle = lpRaw ? monthInputToCycleKey(lpRaw) : undefined;
  } else {
    delete arr[i].lastPaidCycle;
  }
  state.editing[ek] = false;
  syncExpenseUpsert(arr[i], p, type, i);
  render();
}

/**
 * Per item de sinking-fund-teller starten of pauzeren. "Actief sparen" is
 * een opt-in: bij aanmaken staat 'ie uit, want anders toont Florijn
 * fictieve "X/12 in te leggen"-cijfers terwijl de gebruiker er in
 * werkelijkheid nog niets opzij heeft gezet.
 *
 *   - start: zet savingsActive=true + savingsStartedAtCycle=vandaag.
 *   - stop:  zet savingsActive=false; behoudt savingsStartedAtCycle voor
 *            als je later weer wil oppikken (bewust: anders zou een
 *            tweede "start" niet vanaf nu maar weer vanaf onbekend
 *            beginnen — verwarrend).
 */
export function startSavingForItem(type, p, i) {
  if (blockedByKijker()) return;
  const arr = type === 'vast' ? state.S[p].vast : state.S[p].var;
  const item = arr[i];
  if (!item) return;
  item.savingsActive = true;
  item.savingsStartedAtCycle = curKey();
  syncExpenseUpsert(item, p, type, i);
  render();
}

export function stopSavingForItem(type, p, i) {
  if (blockedByKijker()) return;
  const arr = type === 'vast' ? state.S[p].vast : state.S[p].var;
  const item = arr[i];
  if (!item) return;
  if (!confirm(`Sparen voor "${item.n}" pauzeren? De teller wordt op nul gezet — start later weer als 't past.`)) return;
  item.savingsActive = false;
  syncExpenseUpsert(item, p, type, i);
  render();
}

export function delItem(type, p, i) {
  const arr = type === 'vast' ? state.S[p].vast : state.S[p].var;
  const item = arr[i];
  arr.splice(i, 1);
  state.editing = {};
  syncExpenseDelete(item);
  render();
}

export function addItem(type, p, key) {
  const n      = document.getElementById(key + '-n').value.trim();
  const a      = parseFloat(document.getElementById(key + '-a').value) || 0;
  const fVal   = document.getElementById(key + '-f').value;
  const freqM  = fVal === 'custom'
    ? Math.max(1, parseInt(document.getElementById(key + '-fcN').value) || 1)
    : parseInt(fVal) || 1;
  const cat    = document.getElementById(key + '-cat')?.value || '';
  const dom    = document.getElementById(key + '-dom')?.checked || false;
  const lpRaw  = document.getElementById(key + '-lp')?.value;
  const lastPaidCycle = freqM > 1 && lpRaw ? monthInputToCycleKey(lpRaw) : undefined;
  if (!n || !a) return;
  const createdAtCycle = todayKey(p);
  const arr  = type === 'vast' ? state.S[p].vast : state.S[p].var;
  const item = { n, a, freqM, cat, domicilie: dom, createdAtCycle, lastPaidCycle };
  arr.push(item);
  syncExpenseUpsert(item, p, type, arr.length - 1);
  state.addItemModal = null;
  render();
}

// ---------- Bespaar-modal ----------

export function openBespaar() {
  state.modalBespaarOpen = true;
  render();
}
export function closeBespaar() {
  state.modalBespaarOpen = false;
  render();
}

/** Lees alle inputs in de bespaar-modal en update item.a. */
export function saveBespaar() {
  document.querySelectorAll('.modal input[type="number"][data-type][data-i]').forEach(inp => {
    const type = inp.dataset.type;
    const idx  = parseInt(inp.dataset.i);
    const val  = parseFloat(inp.value);
    if (isNaN(val) || val < 0) return;
    const arr = type === 'vast' ? state.S[state.person].vast : state.S[state.person].var;
    if (arr[idx]) {
      arr[idx].a = val;
      syncExpenseUpsert(arr[idx], state.person, type, idx);
    }
  });
  state.modalBespaarOpen = false;
  render();
}

// ---------- Identiteit + kijker-modus ----------

/** Wie ben ik? Schakelt user + persoon (toont meteen eigen data). */
export function setUser(name) {
  if (name !== 'sven' && name !== 'lander') return;
  state.user = name;
  state.person = name;          // standaard tonen we eigen data
  state.activeTab = 'home';
  render();
}

/** Toggle de "gem-storting verloopt automatisch"-flag (zet 't aan of uit
 *  vanuit settings). Geen notice-modal hier — die hoort enkel bij de
 *  oorspronkelijke "Vanaf nu automatisch"-keuze in de notif. */
export function toggleGemAuto(val) {
  if (blockedByKijker()) return;
  const d = state.S[state.user];
  if (!d) return;
  d.gemAuto = !!val;
  syncProfilePatch({ gemAuto: !!val });
  render();
}

/** Idem voor sparen. */
export function toggleSpaarAuto(val) {
  if (blockedByKijker()) return;
  const d = state.S[state.user];
  if (!d) return;
  d.spaarAuto = !!val;
  syncProfilePatch({ spaarAuto: !!val });
  render();
}

/** Mate-meekijken toggle voor de current user. */
export function setShareWithMate(val) {
  const d = state.S[state.user];
  if (!d) return;
  d.shareWithMate = !!val;
  syncProfilePatch({ shareWithMate: !!val });
  render();
}

// ---------- Meldingen-modus + persoon-dropdown ----------

/** Open meldingen-tab (volledig scherm). Sluit dropdown als die open stond.
 *  Forceert state.person → state.user zodat alle actie-handlers (doGem, doSpaar,
 *  surplusKies, openReflect, abonn…) op de eigen account werken. Dat houdt
 *  meldingen strikt account-gebonden — geen verwarring met samen of mate. */
export function openNotifs() {
  state.person = state.user;
  state.editing = {};
  state.pendingAdd = null;
  state.notifsOpen = true;
  state.personMenuOpen = false;
  render();
}

/** Sluit meldingen-tab — terug naar normale weergave. */
export function closeNotifs() {
  state.notifsOpen = false;
  render();
}

/** Klik op de accountnaam in de header:
 *  - In meldingen-modus → sluit meldingen (= terug).
 *  - Anders → toggle de dropdown met persoon-keuzes. */
export function togglePersonMenu() {
  if (state.notifsOpen) {
    state.notifsOpen = false;
    state.personMenuOpen = false;
  } else {
    state.personMenuOpen = !state.personMenuOpen;
  }
  render();
}

/** Sluit de dropdown zonder van persoon te wisselen (klik buiten / Escape). */
export function closePersonMenu() {
  if (!state.personMenuOpen) return;
  state.personMenuOpen = false;
  render();
}

/** Kies een persoon uit de dropdown (= setPerson + dropdown sluiten). */
export function pickPerson(p) {
  state.person = p;
  state.editing = {};
  state.pendingAdd = null;
  state.personMenuOpen = false;
  state.activeTab = p === 'samen' ? 'overzicht' : 'home';
  render();
}

// ---------- Settings-modal ----------

export function openSettings() {
  state.modalSettingsOpen = true;
  state.settingsView = 'menu';
  state.editingCat = null;
  render();
}

export function closeSettings() {
  state.modalSettingsOpen = false;
  state.settingsView = 'menu';
  state.editingCat = null;
  render();
}

/** Schakel naar een sub-view binnen Instellingen. */
export function settingsGoTo(view) {
  state.settingsView = view;
  // Bij navigatie weg van een cat-edit, editingCat clearen.
  if (view !== 'cat') state.editingCat = null;
  render();
}

// ---------- Add-item modal (vast / var / inkomst / droom) ----------

/**
 * Open een focused popup voor het toevoegen van één item.
 *  - type: 'vast' | 'var' | 'inkomst' | 'droom'
 *  - person: relevant voor vast/var/inkomst (bij droom altijd state.person)
 */
export function openAddItem(type, person) {
  if (blockedByKijker()) return;
  state.addItemModal = { type, person: person || state.person };
  render();
}

export function closeAddItem() {
  state.addItemModal = null;
  render();
}

// ---------- Floating action button (FAB) ----------

/** Bepaal op basis van actieve tab/sub-tab welke "item-type" naast een
 *  gewone transactie zinvol is. Returns null voor pure transactie-tabs
 *  (Home, Extra, Samen-overzicht, Meldingen). */
function fabContextKind() {
  if (state.activeTab === 'dromen')    return { kind: 'droom',   label: 'Nieuwe droom' };
  if (state.activeTab === 'inkomsten') return { kind: 'inkomst', label: 'Nieuwe inkomst' };
  if (state.activeTab === 'uitgaven') {
    const sub = state.activeSubTab;
    if (sub === 'vast' || sub === 'svast')    return { kind: 'vast', label: 'Nieuwe vaste kost' };
    if (sub === 'variabel' || sub === 'svar') return { kind: 'var',  label: 'Nieuwe variabele kost' };
  }
  return null;
}

/** Klik op de FAB. Op context-tabs verschijnt eerst een korte keuze
 *  ('Nieuwe transactie' vs 'Nieuw item'); anders direct de transactie-modal. */
export function openFab() {
  if (blockedByKijker()) return;
  if (state.notifsOpen) return;
  // Geen overlap met een al-open modal — voorkomt dubbele-modal-stacks.
  if (state.modalOpen || state.addItemModal || state.modalSettingsOpen ||
      state.modalBespaarOpen || state.modalReflectOpen || state.absencePopupOpen ||
      state.modalAutoNoticeOpen || state.modalSpaarSkipOpen ||
      state.pendingAdd || state.fabPicker) return;

  const ctx = fabContextKind();
  if (!ctx) {
    openAdd();
    return;
  }
  state.fabPicker = ctx;        // { kind, label } — gelezen door buildFabPickerModal
  render();
}

export function closeFabPicker() {
  state.fabPicker = null;
  render();
}

/** Kies in de FAB-picker. action: 'trans' = transactie, 'item' = type-specifiek. */
export function fabPick(action) {
  const ctx = state.fabPicker;
  state.fabPicker = null;
  if (action === 'trans') {
    openAdd();
    return;
  }
  if (ctx?.kind) {
    openAddItem(ctx.kind, state.person);
    return;
  }
  render();
}

/** Zet de FAB-positie (per-device pref). Aangeroepen vanuit de
 *  Rechts/Links-segment in instellingen. */
export function setFabSide(side) {
  import('./lib/fab-pref.js').then(({ writeFabSide }) => {
    writeFabSide(side);
    render();
  });
}

/** ‹ Terug — vanuit cat-edit terug naar de juiste cats-lijst, anders naar menu. */
export function settingsBack() {
  if (state.settingsView === 'cat') {
    const ec = state.editingCat;
    if (ec === '__new_uit')      state.settingsView = 'cats-uit';
    else if (ec === '__new_in')  state.settingsView = 'cats-in';
    else {
      const c = (state.S.cats || []).find(x => x.id === ec);
      state.settingsView = c?.scope === 'in' ? 'cats-in' : 'cats-uit';
    }
    state.editingCat = null;
  } else {
    state.settingsView = 'menu';
    state.editingCat = null;
  }
  render();
}

export function saveAppStart() {
  const v = document.getElementById('set-startdate').value;
  if (!state.S.settings) state.S.settings = {};
  state.S.settings.appStartDate = v || null;
  syncProfilePatch({ appStartDate: v || null });
  render();
}

export function saveTheme(val) {
  if (!state.S.settings) state.S.settings = {};

  // Opt-out flow: kiest de gebruiker expliciet voor 'default' terwijl
  // auto-Pride NU actief is, dan vragen we of we de auto-trigger voor
  // dit jaar moeten uitschakelen. Zo niet → laat 't auto-pride actief,
  // de save heeft dan effectief geen visueel verschil.
  if (val === 'default' && isAutoPrideActive()) {
    const yr = new Date().getFullYear();
    const ok = confirm(
      `🏳️‍🌈 Pride is automatisch actief tot eind juni.\n\nWil je 'm voor de rest van ${yr} uitschakelen? (Volgend jaar trigger 't gewoon opnieuw.)`
    );
    if (ok) setPrideOptOutYear(yr);
    // Bij nee: theme blijft 'default' (geen visueel verschil — auto-pride
    // overheerst nog steeds), maar we slaan 'm wel op zodat de gebruiker
    // niet verrast wordt door een out-of-sync waarde.
  }

  // Kiest de gebruiker expliciet Pride → eventuele opt-out vergeten.
  // Anders zou de toggle "saved=pride + opt-out aan" verwarrend zijn.
  if (val === 'pride') clearPrideOptOut();

  state.S.settings.theme = val;
  syncProfilePatch({ theme: val });
  applyTheme();
  render();
}

export function saveMode(val) {
  if (val !== 'light' && val !== 'dark') return;
  if (!state.S.settings) state.S.settings = {};
  state.S.settings.mode = val;
  syncProfilePatch({ mode: val });
  applyTheme();
  render();
}

/** Toggle tussen maand- en week-weergave van de "Nog over"-tegel op Home. */
export function saveRemainingView(val) {
  if (val !== 'month' && val !== 'week') return;
  if (!state.S.settings) state.S.settings = {};
  state.S.settings.remainingView = val;
  syncProfilePatch({ remainingView: val });
  render();
}

// ---------- Absence-popup (lang weg geweest) ----------

/** "Ja, frisse start" — zet app_start_date op vandaag (lokaal + DB) en
 *  sluit de popup. Bij dev zonder Supabase: alleen lokaal. */
export function resetStartDate() {
  const today = new Date().toISOString().slice(0, 10);
  if (!state.S.settings) state.S.settings = {};
  state.S.settings.appStartDate = today;
  if (supabase && state.userId) {
    updateProfile(state.userId, { app_start_date: today }).catch(console.warn);
  }
  state.absencePopupOpen = false;
  render();
}

/** "Nee, hou bij oude" — sluit alleen de popup, raakt geen data aan.
 *  Komt pas terug bij een volgende absence ≥ 90 dagen vanaf nu (last_seen_at
 *  is door hydrate al gestempeld op nu). */
export function dismissAbsence() {
  state.absencePopupOpen = false;
  render();
}

// ---------- Coaching (Fase E) ----------

/** Open de reflectie-modal voor een specifieke cyclus. Lockt ook max 2
 *  random abonn-items in zodat hun selectie consistent blijft over renders. */
export function openReflect(cycleKey) {
  if (blockedByKijker()) return;
  state.modalReflectCycle = cycleKey;
  state.modalReflectOpen = true;
  const picks = abonnsForReflection(state.person, null);
  state.reflectionAbonnIds = picks
    .map(({ it }) => it.__id)
    .filter(Boolean);
  render();
}

export function closeReflect() {
  state.modalReflectOpen = false;
  state.modalReflectCycle = null;
  state.reflectionAbonnIds = [];
  render();
}

/** Sla over: markeer als 'skipped' zodat de notif verdwijnt voor deze cyclus. */
export function skipReflect(cycleKey) {
  if (blockedByKijker()) return;
  const d = state.S[state.person];
  if (!d.cycleReviews) d.cycleReviews = {};
  // Behoud bestaande velden (bv. overBudgetAcked) — anders verliest een
  // skip alle eerder dismissed budget-meldingen voor deze cyclus.
  d.cycleReviews[cycleKey] = {
    ...(d.cycleReviews[cycleKey] || {}),
    skipped: true,
    completedAt: new Date().toISOString().slice(0, 10)
  };
  if (state.person === state.user) syncProfilePatch({ cycleReviews: d.cycleReviews });
  render();
}

/** Bewaar de reflectie (note + completedAt) en sluit modal. */
export function saveReflect() {
  if (blockedByKijker()) return;
  const cKey = state.modalReflectCycle;
  if (!cKey) return;
  const note = document.getElementById('reflect-note')?.value?.trim() || '';
  const d = state.S[state.person];
  if (!d.cycleReviews) d.cycleReviews = {};
  d.cycleReviews[cKey] = {
    ...(d.cycleReviews[cKey] || {}),
    note,
    completedAt: new Date().toISOString().slice(0, 10)
  };
  if (state.person === state.user) syncProfilePatch({ cycleReviews: d.cycleReviews });
  state.modalReflectOpen = false;
  state.modalReflectCycle = null;
  state.reflectionAbonnIds = [];
  render();
}

/**
 * Dismiss een over-budget-melding voor deze cyclus. Per cat per cyclus
 * geack'd — komt automatisch terug volgende cyclus als de overshoot
 * opnieuw gebeurt. Het reflectie-overzicht negeert deze ack zodat de
 * maandafsluiter nog steeds alles in 't zicht heeft.
 */
export function ackOverBudget(catId, cycleKey) {
  if (blockedByKijker()) return;
  const d = state.S[state.person];
  if (!d.cycleReviews) d.cycleReviews = {};
  const review = d.cycleReviews[cycleKey] || {};
  const acked  = review.overBudgetAcked || {};
  acked[catId] = true;
  d.cycleReviews[cycleKey] = { ...review, overBudgetAcked: acked };
  if (state.person === state.user) syncProfilePatch({ cycleReviews: d.cycleReviews });
  render();
}

/** Bevestig dat een abonnement nog gebruikt wordt → stempel datum. */
export function abonnConfirm(idx) {
  if (blockedByKijker()) return;
  const d = state.S[state.person];
  const it = (d.var || [])[idx];
  if (!it) return;
  it.lastUsedConfirmedAt = new Date().toISOString().slice(0, 10);
  delete it.abonnSkipCycle;
  syncExpenseUpsert(it, state.person, 'var', idx);
  render();
}

/** Verwijder abonnement (met confirm). Hist blijft intact. */
export function abonnRemove(idx) {
  if (blockedByKijker()) return;
  const d = state.S[state.person];
  const it = (d.var || [])[idx];
  if (!it) return;
  if (!confirm(`"${it.n}" verwijderen uit je variabele kosten? (Geboekte uitgaven in de geschiedenis blijven staan.)`)) return;
  d.var.splice(idx, 1);
  syncExpenseDelete(it);
  render();
}

/** Sla over voor deze cyclus — niet opnieuw vragen tot volgende cyclus. */
export function abonnSkip(idx) {
  if (blockedByKijker()) return;
  const d = state.S[state.person];
  const it = (d.var || [])[idx];
  if (!it) return;
  it.abonnSkipCycle = curKey();
  syncExpenseUpsert(it, state.person, 'var', idx);
  render();
}

/** Fase D — opt-in toggle voor pensioensparen-tracking. */
export function togglePensioen(val) {
  if (blockedByKijker()) return;
  if (state.person !== state.user) return;        // kijker-modus
  state.S[state.person].pensioenActief = !!val;
  syncProfilePatch({ pensioenActief: !!val });
  render();
}

/** Fase D — bewaar de fiscale grens voor pensioensparen (1050 of 1350). */
export function savePensioenGrens() {
  if (blockedByKijker()) return;
  if (state.person !== state.user) return;
  const v = parseInt(document.getElementById('set-pensioen-grens')?.value);
  if (v === 1050 || v === 1350) {
    state.S[state.person].pensioenGrens = v;
    syncProfilePatch({ pensioenGrens: v });
    render();
  }
}

/** Fase D — bewaar het default % belasting-reservering. */
export function saveBelastingPct() {
  if (blockedByKijker()) return;
  if (state.person !== state.user) return;
  const v = parseInt(document.getElementById('set-belasting-pct')?.value);
  if (Number.isFinite(v) && v >= 0 && v <= 100) {
    state.S[state.person].belastingPct = v;
    syncProfilePatch({ belastingPct: v });
    render();
  }
}

/**
 * Wipe all transaction-style data — locally én op Supabase. Cats en
 * app-instellingen blijven staan. Bedoeld voor: "ik wil opnieuw beginnen
 * maar mijn setup behouden".
 *
 * Belangrijk: synchroon én DB-first. Als de DB-delete faalt, gaan we
 * NIET door met lokaal wissen — anders zou de hydrate alles meteen
 * terugzetten en de gebruiker een verwarrend halve-reset zien.
 */
export async function resetData() {
  if (!confirm('Zeker dat je al je transactie-data wilt wissen?\n\nDit verwijdert ALLES op Supabase voor jou (en — als je in een huishouden zit — voor Samen): inkomsten, vaste + variabele kosten, geschiedenis, schulden, dromen, weekbudgetten, sparen-vinkjes. Categorieën en app-instellingen blijven staan.\n\nDeze actie is onomkeerbaar.')) return;

  if (!state.userId) {
    // Seed-mode / niet ingelogd → puur lokaal wissen, geen DB-call.
    wipeLocalTransactionData();
    render();
    return;
  }

  try {
    await deleteAllPersonalData(state.userId);
    if (state.householdId) await deleteAllHouseholdData(state.householdId);

    // Profile JSONB-velden ook leeg — anders blijven schulden, gem-handled,
    // spaar-handled, cycle-reviews, etc. via hydrate terugkomen.
    await patchProfile(state.userId, profilePatchStateToDb({
      schulden:        [],
      externeSchulden: [],
      gemHandled:      {},
      spaarHandled:    {},
      loonHandled:     {},
      surplusHandled:  {},
      cycleReviews:    {},
      gemAuto:         false,
      spaarAuto:       false
    }));
  } catch (err) {
    alert('Wissen op Supabase mislukt:\n' + (err?.message || 'onbekende fout') + '\n\nLokale state is NIET aangeraakt — probeer opnieuw of check je internet.');
    return;
  }

  wipeLocalTransactionData();
  render();
}

/** Lokaal deel van resetData. Cats en settings blijven staan. */
function wipeLocalTransactionData() {
  const cats     = state.S.cats;
  const settings = state.S.settings;

  ['sven', 'lander'].forEach(p => {
    const d = state.S[p];
    if (!d) return;
    d.inkomsten = [];
    d.vast = [];
    d.var = [];
    d.hist = {};
    d.schulden = [];
    d.externeSchulden = [];
    d.dromen = [];
    d.surplusHandled = {};
    d.gemAuto = false;
    d.spaarAuto = false;
    d.gemHandled = {};
    d.spaarHandled = {};
    d.loonHandled = {};
    d.cycleReviews = {};
    d.weekBudgets = {};
  });
  if (state.S.samen) {
    state.S.samen.vast = [];
    state.S.samen.var = [];
    state.S.samen.hist = {};
  }
  state.S.cats     = cats;
  state.S.settings = settings;
  state.editing = {};
}

// ---- Cat CRUD ----

export function editCat(id) {
  state.editingCat = id;
  state.settingsView = 'cat';
  render();
}

export function cancelEditCat() {
  settingsBack();
}

export function saveCat(id) {
  const c = state.S.cats.find(x => x.id === id);
  if (!c) return;
  const emoji = document.getElementById('ec-emoji-' + id).value.trim();
  const label = document.getElementById('ec-label-' + id).value.trim();
  const color = document.getElementById('ec-color-' + id).value;
  let changed = false;
  if (label && label !== c.label) { c.label = label; changed = true; }
  if (emoji && emoji !== c.emoji) { c.emoji = emoji; changed = true; }
  if (color && color !== c.color) { c.color = color; changed = true; }
  if (changed) syncCategoryUpsert(c);

  // Weekbudget voor de huidige persoon, alleen voor uit-cats.
  const wbInput = document.getElementById('ec-wb-' + id);
  if (wbInput && state.person !== 'samen' && c.scope === 'uit') {
    const wb = parseFloat(wbInput.value);
    const d = state.S[state.person];
    if (!d.weekBudgets) d.weekBudgets = {};
    if (!isNaN(wb) && wb > 0) {
      d.weekBudgets[id] = wb;
      if (state.person === state.user) syncWeeklyBudget(id, wb);
    } else {
      delete d.weekBudgets[id];
      if (state.person === state.user) syncWeeklyBudgetDelete(id);
    }
  }

  settingsBack();
}

export function openAddCat(scope) {
  state.editingCat = '__new_' + scope;
  state.settingsView = 'cat';
  render();
}

export function addCat(scope) {
  const emoji = document.getElementById('nc-emoji').value.trim() || '📦';
  const label = document.getElementById('nc-label').value.trim();
  const color = document.getElementById('nc-color').value;
  if (!label) return;
  const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '') || 'cat';
  const id   = slug + '_' + Date.now().toString(36);
  const newCat = { id, label, emoji, color, scope };
  state.S.cats.push(newCat);
  syncCategoryUpsert(newCat);

  // Optioneel weekbudget meegegeven? Sla op voor huidige persoon.
  const wbInput = document.getElementById('nc-wb');
  if (wbInput && state.person !== 'samen' && scope === 'uit') {
    const wb = parseFloat(wbInput.value);
    if (!isNaN(wb) && wb > 0) {
      const d = state.S[state.person];
      if (!d.weekBudgets) d.weekBudgets = {};
      d.weekBudgets[id] = wb;
      if (state.person === state.user) syncWeeklyBudget(id, wb);
    }
  }

  settingsBack();
}

export function delCat(id) {
  const c = state.S.cats.find(x => x.id === id);
  if (!c || c.locked) return;
  const fallback = c.scope === 'in' ? 'andere_in' : 'andere_uit';

  // Migreer alle items + hist-entries van deze cat naar fallback.
  // Voor de eigen user syncen we elke gemigreerde rij ook naar Supabase
  // zodat de DB consistent blijft. Partner-data wordt niet bijgewerkt
  // (geen RLS-rechten); die merkt 't bij volgende refresh.
  let touched = 0;
  ['sven', 'lander', 'samen'].forEach(p => {
    const d = state.S[p];
    if (!d) return;
    const isOwn = (p === state.user);
    const isHousehold = (p === 'samen');
    (d.vast || []).forEach((it, idx) => {
      if (it.cat === id) {
        it.cat = fallback;
        touched++;
        if (isOwn || isHousehold) syncExpenseUpsert(it, p, 'vast', idx);
      }
    });
    (d.var || []).forEach((it, idx) => {
      if (it.cat === id) {
        it.cat = fallback;
        touched++;
        if (isOwn || isHousehold) syncExpenseUpsert(it, p, 'var', idx);
      }
    });
    (d.inkomsten || []).forEach((inc, idx) => {
      if (inc.cat === id) {
        inc.cat = fallback;
        touched++;
        if (isOwn) syncIncomeUpsert(inc, idx);
      }
    });
    // hist.ex / hist.in entries: re-sync per cycle-key. Direction 'uit' voor ex, 'in' voor in.
    Object.entries(d.hist || {}).forEach(([ck, h]) => {
      (h.ex || []).forEach(e => {
        if (e.cat === id) {
          e.cat = fallback;
          touched++;
          if (isOwn || isHousehold) syncExtraEntryPush(e, p, 'uit', ck);
        }
      });
      (h.in || []).forEach(e => {
        if (e.cat === id) {
          e.cat = fallback;
          touched++;
          if (isOwn || isHousehold) syncExtraEntryPush(e, p, 'in', ck);
        }
      });
    });
  });

  state.S.cats = state.S.cats.filter(x => x.id !== id);
  syncCategoryDelete(id);
  state.editingCat = null;

  if (touched > 0) {
    alert(`Categorie verwijderd. ${touched} item(s) verplaatst naar "Andere".`);
  }
  render();
}

// ---------- Surplus vorige cyclus ----------

export function surplusKies(ans) {
  const d = state.S[state.person];
  const pk = todayPrevCycleKey();
  if (!d.surplusHandled) d.surplusHandled = {};

  const seg = homeSegs(state.person, pk);
  const amt = Math.max(0, seg.resterend);

  if (ans === 'spaar') {
    // Verdeel over dromen volgens hun gewicht (paused → 0, deadline → effectivePct).
    const tot = d.dromen.reduce((s, dr) => s + droomWeight(dr, state.person), 0) || 1;
    d.dromen.forEach(dr => {
      const w = droomWeight(dr, state.person);
      dr.gesp = Math.min(dr.doel, dr.gesp + amt * w / tot);
    });
    d.surplusHandled[pk] = { kind: 'spaar', amount: amt };
    syncAllDromen();   // dromen.gesp verschoven proportioneel
    // 🎉 Beloning voor de "juiste" keuze — financieel-coachend cadeautje.
    celebrate();
  } else if (ans === 'mee') {
    // Bookkeep als "Rest vorige maand" in de huidige cyclus → eigen
    // slice in de inkomsten-pie en zichtbaar in KPI.
    const t = new Date();
    const cur = todayKey(state.person);
    const h = histFor(state.person, cur);
    const item = {
      a: amt, cat: 'rest_vorig', desc: 'Rest vorige maand',
      date: t.getDate() + '/' + (t.getMonth() + 1) + '/' + t.getFullYear()
    };
    h.in.push(item);
    syncExtraEntryPush(item, state.person, 'in', cur);
    d.surplusHandled[pk] = { kind: 'mee', amount: amt };
  } else {
    d.surplusHandled[pk] = { kind: 'negeer', amount: amt };
  }
  // Persisteer surplus_handled (de hist.in entry / dromen.gesp syncen we hierboven al apart).
  if (state.person === state.user) syncProfilePatch({ surplusHandled: d.surplusHandled });
  render();
}

// ---------- Inkomsten ----------

export function addInk() {
  const k = 'inkomstenadd';
  const n   = document.getElementById(k + '-n').value.trim();
  const a   = parseFloat(document.getElementById(k + '-a').value) || 0;
  const f   = document.getElementById(k + '-f').value;
  const cat = document.getElementById(k + '-cat').value || '';
  const sd  = parseInt(document.getElementById(k + '-sd').value) || 1;
  const ank = document.getElementById(k + '-anker').checked;
  if (!n || !a) return;

  const d = state.S[state.person];
  if (!d.inkomsten) d.inkomsten = [];
  // Anker is exclusief — bestaande anker eerst uitzetten + meteen syncen,
  // anders bots je tegen de partial unique index op anker=true in de DB.
  if (ank) {
    d.inkomsten.forEach((inc, idx) => {
      if (inc.anker) {
        inc.anker = false;
        syncIncomeUpsert(inc, idx);
      }
    });
  }
  const item = { n, a, f, cat, startDag: sd, anker: ank };
  d.inkomsten.push(item);
  syncIncomeUpsert(item, d.inkomsten.length - 1);
  state.addItemModal = null;
  render();
}

export function toggleEditInk(i) {
  const ek = 'ink' + i;
  state.editing[ek] = !state.editing[ek];
  switchTab('inkomsten', document.querySelector('.tab.active'));
}

export function saveInk(i) {
  const d = state.S[state.person];
  const item = d.inkomsten[i];
  if (!item) return;
  const a   = parseFloat(document.getElementById('ein-a-' + i).value);
  const c   = document.getElementById('ein-cat-' + i)?.value;
  const sd  = parseInt(document.getElementById('ein-sd-' + i).value);
  const ank = document.getElementById('ein-anker-' + i).checked;
  if (!isNaN(a) && a > 0) item.a = a;
  if (c) item.cat = c;
  if (!isNaN(sd) && sd >= 1 && sd <= 28) item.startDag = sd;
  if (ank && !item.anker) {
    // Anker is exclusief — bestaande anker eerst uitzetten + syncen,
    // anders bots je tegen de partial unique index op anker=true.
    d.inkomsten.forEach((inc, idx) => {
      if (inc.anker && idx !== i) {
        inc.anker = false;
        syncIncomeUpsert(inc, idx);
      }
    });
    item.anker = true;
  } else if (!ank && item.anker) {
    item.anker = false;
  }
  state.editing['ink' + i] = false;
  syncIncomeUpsert(item, i);
  render();
}

export function delInk(i) {
  const arr = state.S[state.person].inkomsten;
  const item = arr[i];
  arr.splice(i, 1);
  state.editing = {};
  syncIncomeDelete(item);
  render();
}

// ---------- Reminder notifications ----------

export function doGem(ans) {
  const d = state.S[state.person];
  if (!d.gemHandled) d.gemHandled = {};
  d.gemHandled[todayKey(state.person)] = true;
  const patch = { gemHandled: d.gemHandled };
  if (ans === 'auto') {
    d.gemAuto = true;
    patch.gemAuto = true;
    // "Vergeet je bankapp"-modal — Florijn neemt nu aan dat 't vanzelf gaat.
    state.modalAutoNoticeOpen = true;
    state.modalAutoNoticeKind = 'gem';
  }
  if (state.person === state.user) syncProfilePatch(patch);
  render();
}

/** Sluit de "Vergeet je bankapp"-modal. */
export function closeAutoNotice() {
  state.modalAutoNoticeOpen = false;
  state.modalAutoNoticeKind = null;
  render();
}

// ---------- "Deze maand niet sparen"-flow ----------

/** Open de coach-modal (vanuit de spaar-notif). */
export function openSpaarSkip() {
  if (blockedByKijker()) return;
  state.modalSpaarSkipOpen = true;
  state.modalSpaarSkipView = 'tip';
  render();
}

/** Sluit de modal zonder iets te wijzigen. */
export function closeSpaarSkip() {
  state.modalSpaarSkipOpen = false;
  state.modalSpaarSkipView = 'tip';
  render();
}

/** Wissel naar de "kies eigen bedrag"-view. */
export function gotoSpaarCustom() {
  state.modalSpaarSkipView = 'custom';
  render();
}

/** Terug van custom naar tip-view. */
export function spaarSkipBack() {
  state.modalSpaarSkipView = 'tip';
  render();
}

/** "Toch overslaan" — markeer spaarHandled met kind='skip' zodat de notif
 *  verdwijnt voor deze cyclus, en komt automatisch terug in de volgende. */
export function skipSpaar() {
  const d = state.S[state.person];
  if (!d.spaarHandled) d.spaarHandled = {};
  const ck = todayKey(state.person);
  d.spaarHandled[ck] = { kind: 'skip' };
  if (state.person === state.user) syncProfilePatch({ spaarHandled: d.spaarHandled });
  closeSpaarSkip();
}

/** Bewaar een aangepast spaarbedrag voor deze cyclus en verdeel het. */
export function saveSpaarCustom() {
  const raw = document.getElementById('spaar-skip-amt')?.value;
  const amt = parseFloat(raw);
  if (!Number.isFinite(amt) || amt <= 0) {
    alert('Vul een bedrag groter dan 0 in.');
    return;
  }

  const d = state.S[state.person];
  if (!d.spaarHandled) d.spaarHandled = {};
  const ck = todayKey(state.person);
  d.spaarHandled[ck] = { kind: 'custom', amount: amt };

  // Verdeel het ingevoerde bedrag over dromen volgens hun gewicht.
  const tot = (d.dromen || []).reduce((s, dr) => s + droomWeight(dr, state.person), 0) || 1;
  (d.dromen || []).forEach(dr => {
    const w = droomWeight(dr, state.person);
    dr.gesp = Math.min(dr.doel, dr.gesp + amt * w / tot);
  });
  pushEx(state.person, amt, 'sparen', `Aangepast spaarbedrag (€${amt.toFixed(2)})`);
  syncAllDromen();
  if (state.person === state.user) syncProfilePatch({ spaarHandled: d.spaarHandled });

  closeSpaarSkip();
  // 🎉 Ook een vorm van bewust sparen — beloning is op z'n plek.
  celebrate();
}

/**
 * "Loon ontvangen?"-melding beantwoorden.
 *  - 'ja'  → bevestig naturalKey; gated todayKey rolt door + vM/vY volgt mee.
 *  - 'nee' → geen state-wijziging; sluit de meldingen-paneel zodat het
 *            duidelijk is dat de keuze geregistreerd is. De melding komt
 *            terug bij de volgende render — bij twijfel blijft 'm staan.
 */
export function loonOntvangen(ans) {
  const p = state.person;
  const pending = loonPendingCycleKey(p);
  if (!pending) {
    if (ans === 'nee') closeNotifs();
    return;
  }
  if (ans === 'ja') {
    const d = state.S[p];
    if (!d.loonHandled) d.loonHandled = {};
    d.loonHandled[pending] = true;
    if (p === state.user) syncProfilePatch({ loonHandled: d.loonHandled });
    // Spring de viewed cycle mee naar de net-gestarte cyclus zodat de
    // gebruiker direct in de nieuwe maand zit (anders blijft vM/vY op
    // de vorige hangen en lijkt er "niks" gebeurd).
    const [y, m] = pending.split('-').map(Number);
    state.vY = y;
    state.vM = m;
  }
  closeNotifs();
  render();
}

export function doSpaar(ans) {
  const d = state.S[state.person];
  if (!d.spaarHandled) d.spaarHandled = {};
  d.spaarHandled[todayKey(state.person)] = true;
  const patch = { spaarHandled: d.spaarHandled };
  if (ans === 'auto') {
    d.spaarAuto = true;
    patch.spaarAuto = true;
    // "Vergeet je bankapp"-modal — Florijn neemt nu aan dat 't vanzelf gaat.
    state.modalAutoNoticeOpen = true;
    state.modalAutoNoticeKind = 'spaar';
  }

  const amt = spaarbedrag(state.person);   // 20% van vast loon
  const tot = d.dromen.reduce((s, dr) => s + droomWeight(dr, state.person), 0) || 1;
  d.dromen.forEach(dr => {
    const w = droomWeight(dr, state.person);
    dr.gesp = Math.min(dr.doel, dr.gesp + amt * w / tot);
  });
  if (amt > 0) {
    pushEx(state.person, amt, 'sparen', 'Maandelijks spaarbedrag');   // sync via pushEx
  }
  syncAllDromen();
  if (state.person === state.user) syncProfilePatch(patch);
  render();
}

// ---------- Month navigation ----------

export function chMonth(delta) {
  state.vM += delta;
  if (state.vM < 0)  { state.vM = 11; state.vY--; }
  if (state.vM > 11) { state.vM = 0;  state.vY++; }

  // Clamp tussen [today_cycle - 3 cycles, today_cycle] voor de huidige persoon.
  const [maxY, maxM] = todayKey().split('-').map(Number);
  let minM = maxM - 3;
  let minY = maxY;
  while (minM < 0) { minM += 12; minY--; }

  if (state.vY < minY || (state.vY === minY && state.vM < minM)) {
    state.vM += 1;
    if (state.vM > 11) { state.vM = 0; state.vY++; }
  }
  if (state.vY > maxY || (state.vY === maxY && state.vM > maxM)) {
    state.vM -= 1;
    if (state.vM < 0) { state.vM = 11; state.vY--; }
  }

  render();
}

// ---------- Toevoeg-modal ----------

export function openAdd() {
  state.modalOpen = true;
  state.modalDir  = 'uit';
  state.modalRek  = 'eigen';
  state.modalBegrootMode = 'begroot';
  state.modalBegrootItemKey = null;
  state.modalIsDePayment = false;
  state.modalCat = null;
  render();
}

export function closeAdd() {
  state.modalOpen = false;
  state.pendingAdd = null;
  state.modalCat = null;
  state.modalBegrootItemKey = null;
  state.modalIsDePayment = false;
  render();
}

export function setModalDir(d) {
  state.modalDir = d;
  // Rekening defaults aanpassen: 'spaar' bij inkomst betekent 'extra sparen',
  // bij uitgave 'minder gespaard'. Beide blijven geldig — geen reset nodig.
  renderModal();
}

export function setModalRek(r) {
  state.modalRek = r;
  renderModal();
}

/** Begroot-toggle: 'begroot' (vult de item-balk) of 'niet' (off-plan extra). */
export function setModalBegrootMode(mode) {
  state.modalBegrootMode = mode === 'niet' ? 'niet' : 'begroot';
  renderModal();
}

/** Welk vast/var-item wordt aangesproken — alleen relevant wanneer
 *  modalBegrootMode === 'begroot' EN er meerdere matches zijn. */
export function setModalBegrootItem(key) {
  state.modalBegrootItemKey = key || null;
  // Bij wisselen van item: vinkje "Is dit dé betaling?" reset zodat de
  // gebruiker bewust kiest — anders zou een halfjaarlijks item per ongeluk
  // als betaling worden geboekt na wisselen vanaf maandelijks.
  state.modalIsDePayment = false;
  renderModal();
}

/** Voor niet-maandelijkse items (sinking funds): markeer entry als dé
 *  betaling deze cyclus — reset de teller naar 0 + opent overshoot/tekort. */
export function setModalIsDePayment(val) {
  state.modalIsDePayment = !!val;
  renderModal();
}

/** Categorie veranderd → recompute matches en default de toggle:
 *   - matches > 0  → modus 'begroot', eerste match geselecteerd
 *   - matches = 0  → modus 'niet' (toggle wordt verborgen in de UI)
 *  De gebruiker kan daarna manueel switchen. */
export function onModalCatChange(catId) {
  state.modalCat = catId || null;
  const d = state.S[state.person];
  if (!d || !catId) {
    state.modalBegrootMode = 'begroot';
    state.modalBegrootItemKey = null;
    state.modalIsDePayment = false;
    renderModal();
    return;
  }
  const matches = collectMatches(catId);
  if (matches.length > 0) {
    state.modalBegrootMode = 'begroot';
    const first = matches[0];
    state.modalBegrootItemKey = `${first.__type}:${first.__i}`;
  } else {
    state.modalBegrootMode = 'niet';
    state.modalBegrootItemKey = null;
  }
  state.modalIsDePayment = false;
  renderModal();
}

/** Haal alle matching vast+var items op voor een cat. Helper voor
 *  zowel onModalCatChange als saveAdd. */
function collectMatches(catId) {
  const d = state.S[state.person];
  if (!d) return [];
  const v = (d.vast || []).map((it, i) => ({ ...it, __type: 'vast', __i: i }))
                          .filter(it => it.cat === catId);
  const r = (d.var  || []).map((it, i) => ({ ...it, __type: 'var',  __i: i }))
                          .filter(it => it.cat === catId);
  return [...v, ...r];
}

/** Vind het item-object voor een key 'type:index' binnen de huidige matches. */
function findMatchByKey(matches, key) {
  if (!key) return null;
  const [type, idxStr] = key.split(':');
  const idx = parseInt(idxStr, 10);
  return matches.find(m => m.__type === type && m.__i === idx) || null;
}

/**
 * Hoofd-opslag. Splitst per combinatie:
 *  - Uit + eigen → mijn hist().ex (+ optioneel voorschot-schuld)
 *  - Uit + samen → samen hist().ex (+ optioneel leen-schuld)
 *  - Uit + spaar → verlaagt d.spaar deze maand (minder gespaard)
 *  - In  + eigen → mijn hist().in
 *  - In  + samen → samen hist().in
 *  - In  + spaar → mijn hist().in met flag extra_sparen (vloeit later naar dromen — punt 6)
 *
 * Voor Uit+eigen met match aan var-item of weekbudget opent eerst de
 * "begroot vs extra"-submodal (originele gedrag behouden).
 */
export function saveAdd() {
  const amt  = parseFloat(document.getElementById('md-amt').value) || 0;
  const cat  = document.getElementById('md-cat').value;
  const desc = document.getElementById('md-desc').value;
  if (!amt || !cat) return;

  const dir = state.modalDir;
  const rek = state.modalRek;
  const leen      = document.getElementById('md-leen')?.checked || false;
  const voorschot = document.getElementById('md-voorschot')?.checked || false;
  // Datum: standaard vandaag, maar gebruiker kan backdaten om gemiste
  // uitgaven in te halen zonder dat ze "deze week" in het rood gaan.
  const at = readAddDate() || new Date();

  // Inkomst-paden.
  if (dir === 'in') {
    // In + Spaar — geld komt binnen en vloeit meteen naar je dromen, raakt
    // de cyclus niet. Symmetrisch met Uit+Spaar: we boeken zowel een hist.in
    // (de ontvangst, voor de inkomsten-pie) als een hist.ex 'extra_sparen'
    // (de doorboeking naar dromen) zodat 't netto nul is op resterend.
    if (rek === 'spaar') {
      const dd = state.S[state.person];
      const tot = (dd.dromen || []).reduce((s, dr) => s + droomWeight(dr, state.person), 0) || 1;
      (dd.dromen || []).forEach(dr => {
        const w = droomWeight(dr, state.person);
        dr.gesp = Math.min(dr.doel, dr.gesp + amt * w / tot);
      });
      pushIn(state.person, amt, cat, desc, { naarDromen: true, at });
      pushEx(state.person, amt, 'extra_sparen', desc || 'Inkomst naar spaarpot', { naarDromen: true, at });
      syncAllDromen();
      jumpToTodayCycle();
      closeAdd();
      celebrate();
      return;
    }

    if (rek === 'samen') {
      // Vink AAN = ik haal geld VAN de gemeenschappelijke rekening voor
      // m'n lopende maand en moet het Samen later terugbetalen. Geld komt
      // naar MIJN cyclus + 'naar-samen'-schuld.
      // Vink UIT (default) = ik stort iets OP de gemeenschappelijke
      // rekening (refund, extra bijdrage). Geld gaat naar Samen, geen
      // schuld — bestaande gedrag.
      const leenVanSamen = document.getElementById('md-leen-van-samen')?.checked || false;
      if (leenVanSamen) {
        pushIn(state.person, amt, cat, desc, { at });
        addSchuld('naar-samen', amt, cat, desc);
        syncSchulden();
      } else {
        pushIn('samen', amt, cat, desc, { at });
      }
      jumpToTodayCycle();
      closeAdd();
      return;
    }

    // Default = eigen / "naar m'n lopende maand"
    pushIn(state.person, amt, cat, desc, { at });
    // Fase D: belasting-reservering. Vink staat default uit; alleen actief
    // voor persoonlijke inkomsten (Samen heeft geen belasting-context).
    const belChk = document.getElementById('md-bel')?.checked;
    if (belChk) {
      const belPctRaw = parseFloat(document.getElementById('md-bel-pct')?.value);
      const belPct = Number.isFinite(belPctRaw) && belPctRaw > 0 ? belPctRaw : 25;
      const belAmt = Math.round(amt * belPct) / 100;
      if (belAmt > 0) {
        pushEx(state.person, belAmt, 'belasting_uit', `Belastingreservering ${belPct}%: ${desc || cat}`, { at });
      }
    }
    jumpToTodayCycle();
    closeAdd();
    return;
  }

  // Speciaal: extra_sparen is een uitgave die naar dromen vloeit.
  // Distribueer + push als hist.ex zodat 't op de Home-gauge verschijnt.
  if (cat === 'extra_sparen') {
    const dd = state.S[state.person];
    const tot = (dd.dromen || []).reduce((s, dr) => s + droomWeight(dr, state.person), 0) || 1;
    (dd.dromen || []).forEach(dr => {
      const w = droomWeight(dr, state.person);
      dr.gesp = Math.min(dr.doel, dr.gesp + amt * w / tot);
    });
    pushEx(state.person, amt, 'extra_sparen', desc || 'Extra sparen', { naarDromen: true, at });
    syncAllDromen();
    jumpToTodayCycle();
    closeAdd();
    // 🎉 Zelfde reden als bij surplus → spaar: bewuste spaar-keuze verdient beloning.
    celebrate();
    return;
  }

  // Uitgaven van de spaarpot — geen verplichte terugbetaling. Lander
  // gebruikt z'n spaargeld soms en dat is geen "schuld". Wel wordt het
  // getrackt en is er een notif op Home als 't te vaak gebeurt (zie
  // spaarWithdrawalStatus + buildHome).
  if (rek === 'spaar') {
    const dd = state.S[state.person];

    // Aftrek van dromen.gesp proportioneel, paused dromen blijven onaangetast.
    const tot = (dd.dromen || []).reduce((s, dr) => s + droomWeight(dr, state.person), 0) || 1;
    (dd.dromen || []).forEach(dr => {
      const w = droomWeight(dr, state.person);
      dr.gesp = Math.max(0, dr.gesp - amt * w / tot);
    });

    // Push de uitgave + spiegel als hist.in 'uit_spaar' zodat KPI netto
    // klopt EN we via hist.in entries kunnen tellen hoe vaak dit gebeurt.
    pushEx(state.person, amt, cat, desc, { vanSpaar: true, at });
    pushIn(state.person, amt, 'uit_spaar', 'Uit spaarpot', { at });

    jumpToTodayCycle();
    closeAdd();
    return;
  }

  if (rek === 'samen') {
    if (leen) {
      // Geleend: kost is van Sven/Lander, niet van Samen. Per-persoon
      // berekening op Samen mag enkel z'n vast+var meetellen, niet
      // wat iemand vroeger geleend heeft. Schuld bewaart de obligatie.
      pushEx(state.person, amt, cat, desc, { vanSamen: true, at });
      addSchuld('naar-samen', amt, cat, desc);
      syncSchulden();
    } else {
      // Normale gezamenlijke uitgave (rechtstreeks van de samen-kaart).
      pushEx('samen', amt, cat, desc, { at });
    }
    jumpToTodayCycle();
    closeAdd();
    return;
  }

  // Uit + eigen. Begroot vs niet wordt sinds 2026-06-22 inline gekozen
  // bovenaan de modal via de [Begroot] [Niet begroot]-toggle. Geen aparte
  // pickPhase-submodal meer — dat voelde te dwingend voor herhaalde
  // uitgaven (boodschappen) waar je niet bij elke entry een vraag wil.
  //
  // Drie paden:
  //  1) modus 'niet'      → off-plan extra, eet aan de week-tegel.
  //  2) modus 'begroot'   + maandelijks item → vrKey-tag, balk vult.
  //  3) modus 'begroot'   + niet-maandelijks (sinking fund) + "Is dit dé
  //     betaling?" aangevinkt → vrKey + begroot:true, plus overshoot/
  //     tekort-check via pendingAdd.
  const matches = collectMatches(cat);
  const voorschotAndere = document.getElementById('md-voorschot-andere')?.checked || false;

  const applyVoorschotSchulden = () => {
    if (voorschot) addSchuld('van-samen', amt, cat, desc);
    if (voorschotAndere) {
      const other = state.person === 'sven' ? 'lander'
                  : state.person === 'lander' ? 'sven' : null;
      if (other) {
        const linkId = newSchuldId();
        addSchuld('van-andere', amt, cat, desc, { andere: other, id: linkId });
        addSchuldFor(other, 'naar-andere', amt, cat, desc, {
          andere: state.person, id: linkId
        });
      }
    }
    if (voorschot || voorschotAndere) syncSchulden();
  };

  const useBegroot = state.modalBegrootMode === 'begroot' && matches.length > 0;
  const picked = useBegroot ? findMatchByKey(matches, state.modalBegrootItemKey) : null;

  if (!useBegroot || !picked) {
    // Pad 1: off-plan (toggle op 'niet', of geen matches voor deze cat).
    pushEx(state.person, amt, cat, desc, { at });
    applyVoorschotSchulden();
    jumpToTodayCycle();
    closeAdd();
    return;
  }

  if (freqM(picked) === 1) {
    // Pad 2: maandelijks item — tag met vrKey, niet "begroot:true". Voor
    // maandelijkse items heeft begroot=true geen extra semantiek; het zou
    // alleen verwarrend zijn voor toekomstige interpretatie (begroot=true
    // is specifiek voor sinking-fund-reset).
    pushEx(state.person, amt, cat, desc, {
      vrKey: `${picked.__type}:${picked.__i}`,
      at
    });
    applyVoorschotSchulden();
    jumpToTodayCycle();
    closeAdd();
    return;
  }

  // Pad 3: niet-maandelijks (sinking fund).
  if (!state.modalIsDePayment) {
    // Wel gelinkt maar geen "officiële betaling": tagt aan het item zodat
    // spentPerItem het meetelt, maar geen begroot:true → de sinking-fund-
    // teller blijft staan. Gebruikssituatie: bv. extra bijbestelling van
    // hetzelfde abonnement zonder dat dit als jaarvergelijking telt.
    pushEx(state.person, amt, cat, desc, {
      vrKey: `${picked.__type}:${picked.__i}`,
      at
    });
    applyVoorschotSchulden();
    jumpToTodayCycle();
    closeAdd();
    return;
  }

  // "Dit is dé betaling deze cyclus" aangevinkt → kan overshoot- of tekort-
  // vragen oproepen (price-increase, eenmalig, uit dromen/loon). We reiken
  // pendingAdd aan met phase pre-gecomputeerd zodat confAdd verder gaat.
  const p = {
    amt, cat, desc, matches, voorschot, at,
    pickedItem: picked,
    needsOvershoot: amt > picked.a + 0.005,
    needsTekort: false,
    savedSF: 0
  };
  const sf = sinkingFundFor(state.person, picked.__type, picked.__i);
  p.savedSF = sf.saved;
  p.needsTekort = amt > sf.saved + 0.005;

  if (p.needsOvershoot) {
    p.phase = 'overshoot';
    state.pendingAdd = p;
    state.modalOpen = false;
    render();
    return;
  }
  if (p.needsTekort) {
    p.phase = 'tekort';
    state.pendingAdd = p;
    state.modalOpen = false;
    render();
    return;
  }

  // Geen overshoot, geen tekort → meteen vastleggen.
  pushEx(state.person, amt, cat, desc, {
    begroot: true,
    vrKey: `${picked.__type}:${picked.__i}`,
    at
  });
  applyVoorschotSchulden();
  jumpToTodayCycle();
  closeAdd();
}

/**
 * State-machine voor de begrote-uitgave submodal.
 * Phases: 'pick' → optioneel 'overshoot' → optioneel 'tekort' → finalize.
 *
 *  - 'ann'         → annuleren, niets opslaan
 *  - 'pick'        → leest dropdown, transitioneert naar overshoot/tekort
 *                    of finaliseert direct als geen van beide nodig
 *  - 'higher'/'one-off' → antwoord op overshoot-vraag
 *  - 'loon'/'dromen'    → antwoord op tekort-vraag
 */
export function confAdd(ans) {
  const p = state.pendingAdd;
  if (!p) return;

  if (ans === 'ann') {
    state.pendingAdd = null;
    return render();
  }

  const phase = p.phase || 'pick';

  if (phase === 'pick' && ans === 'pick') {
    const sel = document.getElementById('md-bk-pick')?.value;
    if (sel === '' || sel === undefined) return;

    if (sel === 'extra') {
      state.pendingAdd = null;
      pushEx(state.person, p.amt, p.cat, p.desc, p.at ? { at: p.at } : {});
      if (p.voorschot) {
        addSchuld('van-samen', p.amt, p.cat, p.desc);
        syncSchulden();
      }
      jumpToTodayCycle();
      return render();
    }

    // Item gekozen — bereken of overshoot/tekort sub-vragen nodig zijn.
    const idx  = parseInt(sel);
    const item = p.matches[idx];
    p.pickedItem = item;

    p.needsOvershoot = p.amt > item.a + 0.005;       // tolerantie tegen €0.01-rounding
    p.needsTekort    = false;

    if (freqM(item) > 1) {
      const sf = sinkingFundFor(state.person, item.__type, item.__i);
      p.savedSF      = sf.saved;
      p.needsTekort  = p.amt > sf.saved + 0.005;
    }

    if (p.needsOvershoot) { p.phase = 'overshoot'; return render(); }
    if (p.needsTekort)    { p.phase = 'tekort';    return render(); }
    return finalizePendingAdd();
  }

  if (phase === 'overshoot') {
    p.priceIncrease = (ans === 'higher');
    if (p.priceIncrease) {
      const arr = p.pickedItem.__type === 'vast'
        ? state.S[state.person].vast
        : state.S[state.person].var;
      arr[p.pickedItem.__i].a = p.amt;
    }
    if (p.needsTekort) { p.phase = 'tekort'; return render(); }
    return finalizePendingAdd();
  }

  if (phase === 'tekort') {
    p.tekortSource = ans;   // 'loon' | 'dromen'
    return finalizePendingAdd();
  }
}

/** Voltooi de pending add: push entries, debit dromen als nodig. */
function finalizePendingAdd() {
  const p = state.pendingAdd;
  if (!p || !p.pickedItem) return;
  const item = p.pickedItem;
  state.pendingAdd = null;

  pushEx(state.person, p.amt, p.cat, p.desc, {
    begroot: true,
    vrKey:   `${item.__type}:${item.__i}`,
    ...(p.at ? { at: p.at } : {})
  });
  if (p.voorschot) {
    addSchuld('van-samen', p.amt, p.cat, p.desc);
    syncSchulden();
  }

  // Tekort uit dromen: proportioneel aftrekken volgens pct.
  if (p.needsTekort && p.tekortSource === 'dromen') {
    const shortfall = Math.max(0, p.amt - (p.savedSF || 0));
    const d = state.S[state.person];
    const tot = (d.dromen || []).reduce((s, dr) => s + droomWeight(dr, state.person), 0) || 1;
    (d.dromen || []).forEach(dr => {
      const w = droomWeight(dr, state.person);
      dr.gesp = Math.max(0, dr.gesp - shortfall * w / tot);
    });
  }
  // Tekort uit loon: boek het tekort als off-plan extra. Zonder dit blijft
  // homeSegs hangen op alleen de maand-share — de hoofdboeking is begroot
  // + vrKey en raakt geen "overshoot" omdat spent ≤ item.a. Het werkelijke
  // gat tussen sinking-fund en betaling moet wél deze cyclus zichtbaar zijn,
  // anders zakt het resterend-bedrag niet.
  if (p.needsTekort && p.tekortSource === 'loon') {
    const shortfall = Math.max(0, p.amt - (p.savedSF || 0));
    if (shortfall > 0) {
      const label = (p.desc || item.n || '').trim();
      pushEx(
        state.person, shortfall, p.cat,
        label ? `${label} — tekort uit loon` : 'Tekort uit loon',
        p.at ? { at: p.at } : {}
      );
    }
  }

  jumpToTodayCycle();
  render();
}

/**
 * Spring de viewed cycle naar de cycle waar vandaag in valt voor de
 * huidige persoon. Zo zie je je net-toegevoegde uitgave/inkomst direct
 * in de KPI + pie, ook al keek je daarvoor naar een andere maand.
 */
function jumpToTodayCycle() {
  const [y, m] = todayKey().split('-').map(Number);
  state.vY = y;
  state.vM = m;
}

// ---------- Hist + schuld helpers ----------

/**
 * Push een uitgave naar de juiste hist-bucket.
 *  - `extra.at` (optioneel Date): voor terug-in-de-tijd-boekingen vanuit
 *    de add-modal. Bucket wordt op die datum gerekend zodat een entry
 *    van vorige week ook bij vorige week's weekbudget telt — niet
 *    onterecht "in het rood deze week".
 *  - Default = vandaag.
 *
 * Belangrijke nuance: voor de huidige cyclus van de PERSOON gebruiken we
 * gated todayKey() (= loonHandled-aware) zodat entries tijdens de
 * loondag-overgang in dezelfde cyclus belanden als wat de UI toont.
 * Voor backdated entries kan dat niet — daar gaan we direct via
 * dateToCycleKey van de gekozen datum.
 */
function pushEx(person, amt, cat, desc, extra = {}) {
  const { at, ...rest } = extra;
  const t = at instanceof Date ? at : new Date();
  const isToday = sameYMD(t, new Date());
  const key = (person !== 'samen' && isToday)
    ? todayKey(person)
    : dateToCycleKey(t, ankerDay(person));
  const h = histFor(person, key);
  const item = {
    a: amt, cat, desc,
    date: t.getDate() + '/' + (t.getMonth() + 1) + '/' + t.getFullYear(),
    ...rest
  };
  h.ex.push(item);
  syncExtraEntryPush(item, person, 'uit', key);
}

function pushIn(person, amt, cat, desc, extra = {}) {
  const { at, ...rest } = extra;
  const t = at instanceof Date ? at : new Date();
  const isToday = sameYMD(t, new Date());
  const key = (person !== 'samen' && isToday)
    ? todayKey(person)
    : dateToCycleKey(t, ankerDay(person));
  const h = histFor(person, key);
  const item = {
    a: amt, cat, desc,
    date: t.getDate() + '/' + (t.getMonth() + 1) + '/' + t.getFullYear(),
    ...rest
  };
  h.in.push(item);
  syncExtraEntryPush(item, person, 'in', key);
}

/** Twee datums op dezelfde Y/M/D — local time. */
function sameYMD(a, b) {
  return a.getFullYear() === b.getFullYear()
      && a.getMonth()    === b.getMonth()
      && a.getDate()     === b.getDate();
}

/** Lees de date-input uit het add-modal. Returns Date of null bij ongeldig. */
function readAddDate() {
  const el = document.getElementById('md-date');
  const raw = el?.value;
  if (!raw) return null;
  const [y, m, d] = raw.split('-').map(Number);
  if (!y || !m || !d) return null;
  // Lokale middag-tijd zodat DST/UTC niet per ongeluk een dag terugschuift.
  return new Date(y, m - 1, d, 12, 0, 0);
}

function newSchuldId() {
  return `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

function addSchuldFor(person, kant, amt, cat, desc, extra = {}) {
  const d = state.S[person];
  if (!d) return null;
  if (!d.schulden) d.schulden = [];
  const t = new Date();
  const entry = {
    kant, cat, desc,
    bedrag: amt,
    rest:   amt,
    date:   t.getDate() + '/' + (t.getMonth() + 1) + '/' + t.getFullYear(),
    createdAtCycle: todayKey(person),
    termijn: null,
    lastDeductCycle: null,
    ...extra
  };
  d.schulden.push(entry);
  return entry;
}

function addSchuld(kant, amt, cat, desc, extra = {}) {
  return addSchuldFor(state.person, kant, amt, cat, desc, extra);
}

/**
 * Sync veranderingen aan een schuld naar z'n gelinkte tegenhanger
 * op de andere persoon (via gedeeld `id`). Beide kanten houden
 * dezelfde rest/termijn/kwijtgescholden.
 */
function syncLinkedSchuld(s) {
  if (!s.id || !s.andere) return;
  const list = state.S[s.andere]?.schulden;
  if (!list) return;
  const match = list.find(x => x.id === s.id);
  if (!match) return;
  match.rest = s.rest;
  match.termijn = s.termijn;
  match.lastDeductCycle = s.lastDeductCycle;
  match.kwijtgescholden = s.kwijtgescholden;
}

/** Bewerk-modus voor een schuld toggle. Sleutel = schuld.id of index. */
export function toggleEditSchuld(i) {
  const d = state.S[state.person];
  const s = d.schulden?.[i];
  if (!s) return;
  const key = 'schuld-' + (s.id || `i${i}`);
  state.editing[key] = !state.editing[key];
  render();
}

/** Termijn (€/maand) instellen voor een schuld. 0 of leeg → null (= geen auto-aflos). */
export function setTermijn(i) {
  const d = state.S[state.person];
  const s = d.schulden?.[i];
  if (!s) return;
  const inp = document.getElementById(`schuld-termijn-${i}`);
  const v = parseFloat(inp?.value);
  s.termijn = (isNaN(v) || v <= 0) ? null : v;
  // Sluit edit-mode na save zodat de read-only weergave terugkomt.
  const key = 'schuld-' + (s.id || `i${i}`);
  state.editing[key] = false;
  syncLinkedSchuld(s);
  syncSchulden();
  render();
}

/**
 * Kwijtschelden — alleen voor van-andere kant (creditor vergeeft de schuld).
 * Beide kanten worden op kwijtgescholden + rest=0 gezet via sync.
 */
export function kwijtschelden(i) {
  const d = state.S[state.person];
  const s = d.schulden?.[i];
  if (!s || s.kant !== 'van-andere') return;
  const naam = s.andere ? s.andere[0].toUpperCase() + s.andere.slice(1) : 'De andere';
  if (!confirm(`Schuld van €${s.rest.toFixed(2)} kwijtschelden? ${naam} hoeft niets meer terug te betalen.`)) return;
  s.kwijtgescholden = true;
  s.rest = 0;
  syncLinkedSchuld(s);
  syncSchulden();
  render();
}

/**
 * Aflossen — bedrag dat afgehouden wordt = termijn (als ingesteld) of
 * het volledige resterend bedrag. Elke klik telt mee in de Pie als
 * categorie 'terugbet_uit' (uit) of 'terugbet' (in, voor van-samen).
 *
 *  - naar-spaar: ook dromen herstellen proportioneel (geld komt terug)
 *  - naar-samen / naar-andere: pushEx terugbet_uit (jouw uitgave)
 *  - van-samen: pushIn terugbet (jouw inkomst — Samen betaalt je)
 *  - van-andere: gebruikt kwijtschelden ipv aflossen, niet hier
 */
export function aflossen(i) {
  const d = state.S[state.person];
  const s = d.schulden?.[i];
  if (!s || s.rest <= 0) return;

  const amt = s.termijn && s.termijn > 0 ? Math.min(s.termijn, s.rest) : s.rest;
  s.rest -= amt;
  s.lastDeductCycle = todayKey(state.person);

  if (s.kant === 'naar-spaar') {
    const tot = (d.dromen || []).reduce((sum, dr) => sum + droomWeight(dr, state.person), 0) || 1;
    (d.dromen || []).forEach(dr => {
      const w = droomWeight(dr, state.person);
      dr.gesp = Math.min(dr.doel, dr.gesp + amt * w / tot);
    });
    pushEx(state.person, amt, 'terugbet_uit', 'Aflossen naar spaarpot');
  } else if (s.kant === 'naar-samen') {
    pushEx(state.person, amt, 'terugbet_uit', 'Aflossen aan Samen');
  } else if (s.kant === 'naar-andere') {
    const naam = s.andere ? s.andere[0].toUpperCase() + s.andere.slice(1) : 'andere';
    pushEx(state.person, amt, 'terugbet_uit', `Aflossen aan ${naam}`);
  } else if (s.kant === 'van-samen') {
    pushIn(state.person, amt, 'terugbet', 'Terugbetaling van Samen');
  }

  syncLinkedSchuld(s);
  syncSchulden();
  render();
}

export function delExtra(i) {
  const arr = hist().ex;
  const item = arr[i];
  arr.splice(i, 1);
  syncExtraEntryDelete(item);
  render();
}

/** Open/sluit inline edit op een extra-entry. */
export function toggleEditExtra(i) {
  if (blockedByKijker()) return;
  const k = 'extra-' + i;
  state.editing[k] = !state.editing[k];
  render();
}

/** Sla bewerking op: bedrag, omschrijving, categorie, datum, begroot-koppeling. */
export function saveExtra(i) {
  if (blockedByKijker()) return;
  const item = hist().ex?.[i];
  if (!item) return;
  const amtRaw  = document.getElementById('ex-a-' + i)?.value;
  const desc    = document.getElementById('ex-d-' + i)?.value;
  const cat     = document.getElementById('ex-c-' + i)?.value;
  const dateRaw = document.getElementById('ex-dt-' + i)?.value;
  const vrRaw   = document.getElementById('ex-vr-' + i)?.value;

  const amt = parseFloat(amtRaw);
  if (Number.isFinite(amt) && amt > 0) item.a = amt;
  if (typeof desc === 'string') item.desc = desc.trim();
  if (cat) item.cat = cat;
  if (dateRaw) {
    const [y, m, d] = dateRaw.split('-').map(Number);
    if (y && m && d) item.date = `${d}/${m}/${y}`;
  }
  // Koppeling aan gepland item: leeg → off-plan (vrKey + begroot weg).
  // Gevulde key wordt enkel aanvaard als het planned item nog bestaat én
  // dezelfde cat heeft als de (mogelijk net aangepaste) entry.
  if (typeof vrRaw === 'string') {
    if (vrRaw === '') {
      delete item.vrKey;
      delete item.begroot;
    } else {
      const [vType, vIdxStr] = vrRaw.split(':');
      const vIdx = parseInt(vIdxStr, 10);
      const planned = state.S[state.person]?.[vType]?.[vIdx];
      if (planned && planned.cat === item.cat) {
        item.vrKey = vrRaw;
      } else {
        // Cat is na bewerking niet langer compatibel → koppeling weghalen.
        delete item.vrKey;
        delete item.begroot;
      }
    }
  }

  state.editing['extra-' + i] = false;
  // Upsert via dezelfde wrapper als push — direction is 'uit' want hist.ex.
  syncExtraEntryPush(item, state.person, 'uit', curKey());
  render();
}

// ---------- Variabele inkomsten (hist.in entries) ----------

export function delVarInk(i) {
  if (blockedByKijker()) return;
  const arr = hist().in;
  if (!arr) return;
  const item = arr[i];
  if (!item) return;
  if (!confirm('Deze variabele inkomst verwijderen?')) return;
  arr.splice(i, 1);
  syncExtraEntryDelete(item);
  render();
}

export function toggleEditVarInk(i) {
  if (blockedByKijker()) return;
  const k = 'varin-' + i;
  state.editing[k] = !state.editing[k];
  render();
}

export function saveVarInk(i) {
  if (blockedByKijker()) return;
  const item = hist().in?.[i];
  if (!item) return;
  const amtRaw  = document.getElementById('vin-a-' + i)?.value;
  const desc    = document.getElementById('vin-d-' + i)?.value;
  const cat     = document.getElementById('vin-c-' + i)?.value;
  const dateRaw = document.getElementById('vin-dt-' + i)?.value;

  const amt = parseFloat(amtRaw);
  if (Number.isFinite(amt) && amt > 0) item.a = amt;
  if (typeof desc === 'string') item.desc = desc.trim();
  if (cat) item.cat = cat;
  if (dateRaw) {
    const [y, m, d] = dateRaw.split('-').map(Number);
    if (y && m && d) item.date = `${d}/${m}/${y}`;
  }

  state.editing['varin-' + i] = false;
  syncExtraEntryPush(item, state.person, 'in', curKey());
  render();
}

// ---------- Dromen (savings goals) ----------

export function setPct(i, v) {
  const dr = state.S[state.person].dromen[i];
  dr.pct = parseInt(v) || 0;
  syncSavingsGoalUpsert(dr, i);
  switchTab('dromen', document.querySelector('.tab.active'));
}

export function delDroom(i) {
  const arr = state.S[state.person].dromen;
  const item = arr[i];
  arr.splice(i, 1);
  syncSavingsGoalDelete(item);
  render();
}

/** Pauzeer/hervat een droom — paused dromen krijgen geen distributie. */
export function togglePause(i) {
  const dr = state.S[state.person].dromen?.[i];
  if (!dr) return;
  if (dr.isNoodfonds) return;     // noodfonds mag nooit op pauze
  dr.paused = !dr.paused;
  syncSavingsGoalUpsert(dr, i);
  render();
}

/**
 * Droom "innen": je hebt 'm gebruikt (vakantie geboekt, fiets gekocht).
 * Verwijdert de droom uit de lijst. Geen hist-push want sparen is al
 * maandelijks geregistreerd — de spaarbedragen zaten al in hist.ex met
 * cat='sparen', een extra push zou dubbel tellen.
 */
export function innenDroom(i) {
  const d = state.S[state.person];
  const dr = d.dromen[i];
  if (!dr) return;
  if (!confirm(`"${dr.n}" innen — je gebruikt €${dr.gesp.toFixed(2)} en de droom verdwijnt. Doorgaan?`)) return;
  d.dromen.splice(i, 1);
  syncSavingsGoalDelete(dr);
  render();
}

export function addDroom() {
  const n = document.getElementById('dn').value.trim();
  const d = parseFloat(document.getElementById('dd').value) || 0;
  const g = parseFloat(document.getElementById('dg').value) || 0;
  const p = parseInt(document.getElementById('dp').value)   || 0;
  const c = document.getElementById('dc').value;
  const dl = document.getElementById('dl')?.value || null;
  if (!n || !d) return;
  const item = { n, doel: d, gesp: g, pct: p, c, deadline: dl || undefined };
  const arr = state.S[state.person].dromen;
  arr.push(item);
  syncSavingsGoalUpsert(item, arr.length - 1);
  state.addItemModal = null;
  render();
}

/** Inline deadline-update vanuit de droomrij. */
export function setDeadline(i) {
  const dr = state.S[state.person].dromen?.[i];
  if (!dr) return;
  const v = document.getElementById('dl-' + i)?.value;
  dr.deadline = v || undefined;
  syncSavingsGoalUpsert(dr, i);
  render();
}

/** Open/sluit de bewerk-modus voor een droom (via swipe-edit). */
export function toggleEditDroom(i) {
  if (blockedByKijker()) return;
  const dr = state.S[state.person].dromen?.[i];
  if (!dr) return;
  if (dr.isNoodfonds) return;     // noodfonds heeft eigen controls
  const k = 'droom-' + i;
  state.editing[k] = !state.editing[k];
  render();
}

/** Sla de bewerkte droom op (naam, doel, gespaard, %, deadline, kleur). */
export function saveDroom(i) {
  if (blockedByKijker()) return;
  const dr = state.S[state.person].dromen?.[i];
  if (!dr) return;
  const n  = document.getElementById('edn-' + i)?.value.trim();
  const dd = parseFloat(document.getElementById('edd-' + i)?.value);
  const gg = parseFloat(document.getElementById('edg-' + i)?.value);
  const pp = parseInt(document.getElementById('edp-' + i)?.value);
  const dl = document.getElementById('edl-' + i)?.value;
  const cc = document.getElementById('edc-' + i)?.value;
  if (n) dr.n = n;
  if (Number.isFinite(dd) && dd > 0) dr.doel = dd;
  if (Number.isFinite(gg) && gg >= 0) dr.gesp = Math.min(gg, dr.doel);
  if (Number.isFinite(pp) && pp >= 0 && pp <= 100) dr.pct = pp;
  dr.deadline = dl || undefined;
  if (cc) dr.c = cc;
  state.editing['droom-' + i] = false;
  syncSavingsGoalUpsert(dr, i);
  render();
}

// ---------- Noodfonds (Fase B) ----------

export function markAlsNoodfonds(idx) {
  const d = state.S[state.person];
  if (!d.dromen?.[idx]) return;
  // is_noodfonds is exclusief — bestaande noodfonds eerst uitzetten + syncen,
  // anders bots je tegen de partial unique index in de DB.
  d.dromen.forEach((dr, i) => {
    if (dr.isNoodfonds && i !== idx) {
      dr.isNoodfonds = false;
      syncSavingsGoalUpsert(dr, i);
    }
  });
  d.dromen[idx].isNoodfonds = true;
  syncSavingsGoalUpsert(d.dromen[idx], idx);
  render();
}

export function unmarkNoodfonds() {
  if (blockedByKijker()) return;
  const d = state.S[state.person];
  (d.dromen || []).forEach((dr, i) => {
    if (dr.isNoodfonds) {
      dr.isNoodfonds = false;
      syncSavingsGoalUpsert(dr, i);
    }
  });
  render();
}

/**
 * Spreek het noodfonds aan — typisch bij inkomensgat (tijdelijk werkloos,
 * lager loon, onverwachte grote uitgave). Verlaagt `gesp` met het opgenomen
 * bedrag en boekt datzelfde bedrag als extra inkomst in de huidige cyclus
 * (cat 'extra_in'), zodat het in de KPI's en het pie-chart verschijnt als
 * geld dat je deze maand te besteden hebt. Runway daalt automatisch mee
 * en de "onder 1 maand vaste lasten"-alarm vuurt vanzelf weer als nodig.
 */
export function aanspreekNoodfonds() {
  if (blockedByKijker()) return;
  const d = state.S[state.person];
  const idx = (d.dromen || []).findIndex(dr => dr.isNoodfonds);
  if (idx < 0) return;
  const nf = d.dromen[idx];
  if (!(nf.gesp > 0)) return;

  const amtStr = prompt(`Hoeveel opnemen uit je noodfonds? (beschikbaar: €${nf.gesp.toFixed(2)})`);
  if (amtStr === null) return;
  const amt = parseFloat(String(amtStr).replace(',', '.'));
  if (Number.isNaN(amt) || amt <= 0) {
    alert('Ongeldig bedrag.');
    return;
  }
  if (amt > nf.gesp + 0.005) {
    alert(`Je noodfonds heeft maar €${nf.gesp.toFixed(2)}. Pas het bedrag aan.`);
    return;
  }

  const reden = prompt('Waarvoor? (kort, bv. "Tijdelijk werkloos", "Inkomensgat juli", "Auto kapot")', 'Inkomensgat');
  if (reden === null) return;
  const r = reden.trim() || 'Noodfonds aanspraak';

  nf.gesp = Math.max(0, nf.gesp - amt);

  const cKey = curKey();
  const h = histFor(state.person, cKey);
  const t = new Date();
  h.in.push({
    a: amt,
    cat: 'extra_in',
    desc: 'Uit noodfonds: ' + r,
    date: t.getDate() + '/' + (t.getMonth() + 1) + '/' + t.getFullYear()
  });

  render();
}

/**
 * Zet tijdelijk 100% spaarcenten naar het noodfonds (overige dromen → 0%).
 * Bewaart een snapshot van de oorspronkelijke pct's per droom-id (stabiel
 * over herorderingen), zodat de gebruiker kan herstellen via één klik.
 * Snapshot wordt enkel gemaakt bij de eerste activatie — handmatige
 * tussentijdse aanpassingen overschrijven 'm niet.
 */
export function tijdelijk100Noodfonds() {
  if (blockedByKijker()) return;
  const d = state.S[state.person];
  const idx = (d.dromen || []).findIndex(dr => dr.isNoodfonds);
  if (idx < 0) return;
  if (!confirm('Tijdelijk 100% van je spaarcenten naar het noodfonds — overige dromen krijgen 0%. Doorgaan? (Je kan dit altijd herstellen met de knop "Herstel verdeling".)')) return;
  if (!d.noodfondsPctSnapshot) {
    d.noodfondsPctSnapshot = {};
    (d.dromen || []).forEach((dr) => {
      // Sleutel op droom-naam want dromen hebben geen stabiele id.
      // Bij duplicate names valt de tweede in dezelfde bucket — acceptabel
      // voor zo'n zeldzame edge case.
      d.noodfondsPctSnapshot[dr.n] = dr.pct;
    });
  }
  d.dromen.forEach((dr, i) => { dr.pct = i === idx ? 100 : 0; });
  // Persistentie: de pct-mutaties (per droom-rij) én de snapshot (op profile).
  // Zonder dit was de burst stuk bij refresh — restore-banner verdween mee.
  syncAllDromen();
  syncProfilePatch({ noodfondsPctSnapshot: d.noodfondsPctSnapshot });
  render();
}

/**
 * Herstelt de droom-pct-verdeling van vóór tijdelijk100Noodfonds().
 * Snapshot wordt gewist na herstel.
 */
export function herstelDroomVerdeling() {
  if (blockedByKijker()) return;
  const d = state.S[state.person];
  const snap = d.noodfondsPctSnapshot;
  if (!snap) return;
  if (!confirm('De droom-verdeling terugzetten naar wat het was vóór "tijdelijk 100% naar noodfonds"?')) return;
  (d.dromen || []).forEach(dr => {
    if (snap[dr.n] !== undefined) dr.pct = snap[dr.n];
  });
  delete d.noodfondsPctSnapshot;
  // Sync: dromen krijgen hun originele pct terug, profile-snapshot → null.
  syncAllDromen();
  syncProfilePatch({ noodfondsPctSnapshot: null });
  render();
}

// ---------- Externe schulden (Fase B) ----------

export function addExterneSchuld() {
  const n      = document.getElementById('esn-n').value.trim();
  const hs     = parseFloat(document.getElementById('esn-hs').value) || 0;
  const rRaw   = document.getElementById('esn-r').value;
  const rest   = rRaw === '' ? hs : (parseFloat(rRaw) || 0);
  const rente  = parseFloat(document.getElementById('esn-rt').value) || 0;
  const minM   = parseFloat(document.getElementById('esn-mm').value) || 0;
  const sd     = document.getElementById('esn-sd').value || null;
  if (!n || rest <= 0 || rente < 0) return;
  const d = state.S[state.person];
  if (!d.externeSchulden) d.externeSchulden = [];
  d.externeSchulden.push({
    id: Date.now(),
    n, hoofdsom: hs > 0 ? hs : rest,
    rest, rente, minMaand: minM, startDate: sd
  });
  syncExterneSchulden();
  render();
}

export function editExterneSchuld(id) {
  const k = 'extschuld-' + id;
  state.editing[k] = !state.editing[k];
  render();
}

export function saveExterneSchuld(id) {
  const d = state.S[state.person];
  const s = (d.externeSchulden || []).find(x => x.id === id);
  if (!s) return;
  const n   = document.getElementById('es-n-' + id).value.trim();
  const hs  = parseFloat(document.getElementById('es-hs-' + id).value);
  const r   = parseFloat(document.getElementById('es-r-' + id).value);
  const rt  = parseFloat(document.getElementById('es-rt-' + id).value);
  const mm  = parseFloat(document.getElementById('es-mm-' + id).value);
  if (n)              s.n = n;
  if (!isNaN(hs))     s.hoofdsom = hs;
  if (!isNaN(r))      s.rest = r;
  if (!isNaN(rt))     s.rente = rt;
  if (!isNaN(mm))     s.minMaand = mm;
  state.editing['extschuld-' + id] = false;
  syncExterneSchulden();
  render();
}

export function delExterneSchuld(id) {
  const d = state.S[state.person];
  const s = (d.externeSchulden || []).find(x => x.id === id);
  if (!s) return;
  if (!confirm(`"${s.n}" verwijderen? Dit wist enkel de tracking — niet de schuld zelf.`)) return;
  d.externeSchulden = d.externeSchulden.filter(x => x.id !== id);
  syncExterneSchulden();
  render();
}

/**
 * Aflossing op een externe schuld: vraag bedrag via prompt(), trek af van
 * rest, en log als hist.ex met cat 'andere_uit' (geen aparte cat — simpel).
 * Desc maakt de bron duidelijk in de geschiedenis.
 */
export function aflossenExterneSchuld(id) {
  const d = state.S[state.person];
  const s = (d.externeSchulden || []).find(x => x.id === id);
  if (!s || s.rest <= 0) return;
  const def = Math.min(s.minMaand || s.rest, s.rest);
  const raw = prompt(`Hoeveel aflossen op "${s.n}"? (Openstaand: €${s.rest.toFixed(2)})`, def.toFixed(2));
  if (raw === null) return;
  const amt = parseFloat(raw);
  if (isNaN(amt) || amt <= 0) return;
  const real = Math.min(amt, s.rest);
  s.rest -= real;
  pushEx(state.person, real, 'andere_uit', `Aflossing externe schuld: ${s.n}`);
  syncExterneSchulden();
  render();
}

// ---------- Onverwacht-trend (Fase B) ----------

export function onverwachtNaarVast() {
  switchSubTab(state.person === 'samen' ? 'svast' : 'vast');
}

export function onverwachtNegeer() {
  const d = state.S[state.person];
  if (!d) return;
  d.onverwachtTrendIgnored = curKey();
  render();
}

// ---------- Expose to window for inline handlers ----------

/**
 * The inline `onclick` attributes in panel template strings reference
 * these by name. ES modules are isolated by default, so we explicitly
 * publish them on window. Replace this with addEventListener wiring
 * when the templates are refactored to data attributes.
 */
/**
 * Toggle:
 *  - "Anders…" custom-input visibility (zichtbaar bij value='custom')
 *  - "Laatst betaald"-veld visibility (verborgen bij value='1', anders zichtbaar)
 */
function onFreqChange(selectEl, customDivId, nonMonthlyDivId) {
  const el = document.getElementById(customDivId);
  if (el) el.style.display = selectEl.value === 'custom' ? 'block' : 'none';
  if (nonMonthlyDivId) {
    const el2 = document.getElementById(nonMonthlyDivId);
    if (el2) el2.style.display = selectEl.value === '1' ? 'none' : 'block';
  }
}

/**
 * Globale Escape-handler — sluit de bovenste open modal/overlay.
 * Eén keer registreren (vanuit main.js). Volgorde gaat van diepste
 * sub-modal (Reflect) naar buitenste view (notifs-modus).
 *
 * Pending-add submodal: state.pendingAdd is een tijdelijke begroot-keuze;
 * op Escape gewoon clearen + render.
 */
let escapeHandlerInstalled = false;
export function installEscapeHandler() {
  if (escapeHandlerInstalled) return;
  escapeHandlerInstalled = true;
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (state.modalSpaarSkipOpen) { closeSpaarSkip(); return; }
    if (state.modalAutoNoticeOpen) { closeAutoNotice(); return; }
    if (state.absencePopupOpen)   { dismissAbsence(); return; }
    if (state.modalReflectOpen)   { closeReflect();   return; }
    if (state.modalBespaarOpen)   { closeBespaar();   return; }
    if (state.modalSettingsOpen)  { closeSettings();  return; }
    if (state.addItemModal)       { closeAddItem();   return; }
    if (state.pendingAdd)         { state.pendingAdd = null; render(); return; }
    if (state.modalOpen)          { closeAdd();       return; }
    if (state.personMenuOpen)     { closePersonMenu(); return; }
    if (state.notifsOpen)         { closeNotifs();    return; }
  });
}

// ---------- Huishouden-beheer (vanuit instellingen) ----------

/** Maak een huishouden aan vanuit settings (zonder-huishouden-mode in viewHousehold).
 *  Gebruikt dezelfde SECURITY DEFINER RPC als de setup-gate. */
export async function createHouseholdFromSettings() {
  if (blockedByKijker()) return;
  if (!supabase) return;
  const nameInput = document.getElementById('set-hh-name');
  const name = (nameInput?.value || '').trim() || `${state.user[0].toUpperCase() + state.user.slice(1)} & co`;
  const displayName = state.user[0].toUpperCase() + state.user.slice(1);
  try {
    const { id } = await setupNewHousehold(displayName, name);
    state.householdId = id;
    state.householdName = name;
    state.mateConnected = false;
    render();
  } catch (err) {
    alert('Aanmaken mislukt: ' + (err?.message || 'onbekende fout'));
  }
}

/** Start de welkomst-tour opnieuw vanuit de instellingen. Sluit de
 *  settings-modal eerst zodat de tour de echte UI onder zich heeft. */
export function replayOnboarding() {
  if (blockedByKijker()) return;
  state.modalSettingsOpen = false;
  render();
  // Volgende frame: DOM is opnieuw gerenderd, tour kan targets vinden.
  requestAnimationFrame(() => {
    startOnboarding(() => {
      if (state.userId) {
        patchProfile(state.userId, { onboarding_done: true })
          .catch(err => console.warn('Sync onboarding_done failed:', err));
      }
    });
  });
}

/**
 * Handmatige update-check vanuit Instellingen → User. Schrijft een korte
 * status-tekst onder de knop:
 *   - "updated"  → er stond al een nieuwe versie klaar, toast verschijnt rechtsonder.
 *   - "checking" → registration.update() loopt; pwa-update.js zal `onNeedRefresh`
 *                  triggeren als er iets nieuws is, zo niet, blijft 't stil.
 *   - "unknown"  → geen SW (dev-mode of disabled).
 */
export async function checkAppUpdate(btn) {
  if (btn) {
    btn.disabled = true;
    btn.textContent = '↻ Bezig met checken…';
  }
  const out = document.getElementById('pwa-check-result');
  try {
    const status = await checkForUpdateNow();
    if (out) {
      if (status === 'updated') {
        out.textContent = '✨ Nieuwe versie klaar — zie melding rechtsonder.';
      } else if (status === 'checking') {
        out.textContent = 'Je bent up-to-date. (Of er komt zo een melding als er toch iets klaar staat.)';
      } else {
        out.textContent = 'Geen service worker actief — updates lopen via een gewone reload.';
      }
    }
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.textContent = '↻ Check op updates';
    }
  }
}

/** Toggle "We hebben een gezamenlijke rekening" — toont/verbergt Samen-view. */
export async function toggleSharedAccount(val) {
  if (blockedByKijker()) return;
  if (!state.householdId) return;
  const next = !!val;
  state.hasSharedAccount = next;
  // Als Samen net uitgezet werd terwijl de gebruiker daar stond — terug naar eigen.
  if (!next && state.person === 'samen') state.person = state.user;

  // Bij aanzetten: zorg dat er een auto-gezamenlijk vast-item bestaat zodat
  // de gem-storting-flow met een nul-bedrag kan starten. refreshAutoItems
  // zet het bedrag correct zodra er Samen-kosten bestaan (50/50-split).
  if (next) {
    const d = state.S[state.user];
    if (d) {
      const hasAuto = (d.vast || []).some(it => it.auto && it.cat === 'gezamenlijk');
      if (!hasAuto) {
        const item = { n: 'Gezamenlijke kosten', a: 0, f: 'm', cat: 'gezamenlijk', auto: true };
        d.vast = d.vast || [];
        d.vast.push(item);
        syncExpenseUpsert(item, state.user, 'vast', d.vast.length - 1);
      }
    }
  }
  render();
  try {
    await updateHousehold(state.householdId, { has_shared_account: next });
  } catch (err) {
    // Rollback bij fout zodat UI niet uit sync raakt met DB.
    state.hasSharedAccount = !next;
    render();
    alert('Opslaan mislukt: ' + (err?.message || 'onbekende fout'));
  }
}

/** Hernoem het huidige huishouden. */
export async function saveHouseholdName() {
  if (blockedByKijker()) return;
  if (!state.householdId) return;
  const v = (document.getElementById('set-hh-name')?.value || '').trim();
  if (!v || v === state.householdName) return;
  try {
    await updateHousehold(state.householdId, { name: v });
    state.householdName = v;
    render();
  } catch (err) {
    alert('Opslaan mislukt: ' + (err?.message || 'onbekende fout'));
  }
}

/** Kopieer de uitnodigingslink naar het klembord, met visuele bevestiging. */
export async function copyInviteFromSettings(btn) {
  const input = document.getElementById('set-invite-link');
  if (!input) return;
  const original = btn?.textContent;
  try {
    await navigator.clipboard.writeText(input.value);
  } catch {
    input.select();
    try { document.execCommand('copy'); } catch {}
  }
  if (btn) {
    btn.textContent = '✓ Gekopieerd';
    setTimeout(() => { if (btn) btn.textContent = original; }, 1500);
  }
}

/** Verlaat het huishouden — zet profile.household_id op null. Samen-data
 *  blijft bestaan in de DB (voor de andere lid), verdwijnt enkel uit jouw zicht. */
export async function leaveHousehold() {
  if (blockedByKijker()) return;
  if (!state.householdId) return;
  if (!confirm('Huishouden verlaten? Je samen-data verdwijnt uit jouw zicht (blijft bestaan voor je medebewoner). Je kan altijd een nieuw huishouden maken of opnieuw aansluiten via een uitnodigingslink.')) return;
  try {
    await updateProfile(state.userId, { household_id: null });
    state.householdId = null;
    state.householdName = '';
    state.mateConnected = false;
    // Maak samen-state leeg zodat de UI geen oude data toont.
    if (state.S.samen) {
      state.S.samen.vast = [];
      state.S.samen.var  = [];
      state.S.samen.hist = {};
    }
    // Als de gebruiker op 'samen' stond → terug naar eigen view.
    if (state.person === 'samen') state.person = state.user;
    render();
  } catch (err) {
    alert('Verlaten mislukt: ' + (err?.message || 'onbekende fout'));
  }
}

/** Open een file-picker om een Florijn-export-bestand te selecteren.
 *  Na parse + bevestiging vervangen we alle DB-data en herladen. */
export function openImportPicker() {
  if (blockedByKijker()) return;
  if (!state.userId) {
    alert('Niet ingelogd — kan niet importeren.');
    return;
  }
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'application/json,.json';
  input.style.display = 'none';
  document.body.appendChild(input);
  input.addEventListener('change', async () => {
    const file = input.files?.[0];
    document.body.removeChild(input);
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = parseImportFile(text);
      const summary = summarizeImport(parsed);
      const ok = confirm(
        `Bestand klaar voor import:\n\n${summary}\n\n` +
        `⚠️ Dit overschrijft ALLE huidige data van jouw account in Supabase. ` +
        `Eventuele wijzigingen sinds de export gaan verloren. Doorgaan?`
      );
      if (!ok) return;
      // Vergrendel UI met simpel alert-modus — een dedicated busy-modal komt
      // eventueel later. Importeren duurt typisch enkele seconden.
      try {
        await applyImport(parsed);
        alert('✅ Import voltooid! De app gaat nu herladen.');
        location.reload();
      } catch (err) {
        alert(
          '❌ Import mislukt: ' + (err?.message || 'onbekende fout') +
          '\n\nJe data is mogelijk inconsistent — refresh de pagina en kijk wat er is gebleven, ' +
          'of probeer opnieuw te importeren.'
        );
      }
    } catch (err) {
      alert('Bestand kon niet gelezen worden: ' + (err?.message || 'onbekende fout'));
    }
  });
  input.click();
}

export function attachToWindow() {
  Object.assign(window, {
    onFreqChange,
    toggleForm, toggleWE, saveWE,
    toggleEdit, saveEdit, delItem, addItem,
    doGem, doSpaar, loonOntvangen, closeAutoNotice,
    openSpaarSkip, closeSpaarSkip, gotoSpaarCustom, spaarSkipBack, skipSpaar, saveSpaarCustom,
    chMonth,
    openAdd, closeAdd, setModalDir, setModalRek, saveAdd, confAdd,
    setModalBegrootMode, setModalBegrootItem, setModalIsDePayment, onModalCatChange,
    aflossen, setTermijn, toggleEditSchuld, kwijtschelden, delExtra, toggleEditExtra, saveExtra,
    addInk, toggleEditInk, saveInk, delInk,
    delVarInk, toggleEditVarInk, saveVarInk,
    surplusKies,
    openSettings, closeSettings, saveAppStart, saveTheme, saveMode, saveRemainingView, resetData,
    resetStartDate, dismissAbsence,
    settingsGoTo, settingsBack,
    openAddItem, closeAddItem,
    openFab, closeFabPicker, fabPick, setFabSide,
    setUser, setShareWithMate, toggleGemAuto, toggleSpaarAuto,
    createHouseholdFromSettings, saveHouseholdName, copyInviteFromSettings, leaveHousehold, toggleSharedAccount,
    replayOnboarding, downloadExport, openImportPicker, checkAppUpdate,
    startSavingForItem, stopSavingForItem,
    openNotifs, closeNotifs, togglePersonMenu, closePersonMenu, pickPerson,
    editCat, cancelEditCat, saveCat, openAddCat, addCat, delCat,
    openBespaar, closeBespaar, saveBespaar,
    setPct, delDroom, innenDroom, addDroom, togglePause, setDeadline,
    toggleEditDroom, saveDroom,
    // Fase B
    markAlsNoodfonds, unmarkNoodfonds, tijdelijk100Noodfonds, herstelDroomVerdeling, aanspreekNoodfonds,
    addExterneSchuld, editExterneSchuld, saveExterneSchuld,
    delExterneSchuld, aflossenExterneSchuld,
    onverwachtNaarVast, onverwachtNegeer,
    // Fase C
    saveHuidigSaldo,
    // Fase D
    savePensioenGrens, saveBelastingPct, togglePensioen,
    // Fase E
    openReflect, closeReflect, skipReflect, saveReflect, ackOverBudget,
    abonnConfirm, abonnRemove, abonnSkip
  });
}

// ---------- Cashflow (Fase C) ----------

/**
 * Bewaart het ingegeven rekeningsaldo en stempelt de update-datum.
 * Een leeg veld → null (zet de cashflow-tegel terug op "nog niet ingesteld").
 */
export function saveHuidigSaldo() {
  const raw = document.getElementById('set-saldo')?.value;
  const d = state.S[state.person];
  if (raw === '' || raw === undefined) {
    d.huidigSaldo = null;
    d.huidigSaldoUpdated = null;
  } else {
    const v = parseFloat(raw);
    if (Number.isNaN(v)) return;
    d.huidigSaldo = v;
    d.huidigSaldoUpdated = new Date().toISOString().slice(0, 10);
  }
  if (state.person === state.user) {
    syncProfilePatch({
      huidigSaldo: d.huidigSaldo,
      huidigSaldoUpdated: d.huidigSaldoUpdated
    });
  }
  render();
}
